import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, backendConfig } from '../config/backendConfig';
// Client does not write membership; backend updates Firestore
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { CONSENT_MAPPING } from '../components/signup/consentQuestionsSimple';
import SimpleConsentForm from '../components/signup/SimpleConsentForm';
import { saveConsentSettings } from '../components/signup/ConsentService';
import artmogalleryBackground from '../assets/artmogallery.png';
import KidBackground from '../ui/backgrounds/KidBackground';
import MentorSelection from '../components/MentorSelection';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';
import { calculateSeatPricing } from '../utils/billingUtils';
import { getPlanConfig, planKey } from '../config/pricingConfig';
import { authFetch } from '../utils/authFetch';
import { redeemArtmoCodes, fetchPublicMembershipConfig } from '../services/studioOnboardingApi';
import { X } from 'lucide-react';
// Student/Parent plans (feature sets only) – pricing pulled from config

function monthsInBillingPeriod(billingInterval) {
  if (billingInterval === 'yearly') return 12;
  if (billingInterval === 'semiannual') return 6;
  if (billingInterval === 'quarterly') return 3;
  return 1;
}

function periodLabelForBilling(billingInterval) {
  if (billingInterval === 'yearly') return 'year';
  if (billingInterval === 'semiannual') return '6 months';
  if (billingInterval === 'quarterly') return '3 months';
  return 'month';
}

/** Shown when user adds Unlimited Studio to a paid subscription checkout */
const STUDIO_UNLIMITED_ADDON_BULLETS = [
  'Higher limits on Art History, Compare, and Style Transfer',
  'Storyboarding — “Once Upon a Click”',
  'Full creative toolkit for your subscription period',
  'Everything in standard Studio access, unlocked end-to-end',
];

const studentPlans = [
  {
    id: 'artmocode',
    name: 'Books → free Studio',
    requiresPayment: false,
    features: {
      artHistoryAppreciation: { enabled: true, limit: 5 },
      artComparison: { enabled: false, limit: 0 },
      styleTransfer: { enabled: true, limit: 3, tutorial: false },
      dailyActivities: { enabled: true, limit: 3 },
      individualArtAppreciation: { enabled: true, limit: 3 },
      coLearningSpace: { enabled: true, limit: 'unlimited' },
      reimagineStudio: { enabled: true, limit: 'unlimited' },
      storyboarding: { enabled: false, limit: 0 },
    },
  },
  {
    id: 'basic',
    name: 'Artmosphere Studio',
    requiresPayment: true,
    features: {
      artHistoryAppreciation: { enabled: true, limit: 15 },
      artComparison: { enabled: true, limit: 'unlimited' },
      styleTransfer: { enabled: true, limit: 15, tutorial: true },
      dailyActivities: { enabled: true, limit: 'unlimited' },
      individualArtAppreciation: { enabled: true, limit: 10 },
      coLearningSpace: { enabled: true, limit: 'unlimited' },
      reimagineStudio: { enabled: true, limit: 'unlimited' },
      storyboarding: { enabled: false, limit: 0 },
    },
  },
];

// Mentor plans template (pricing will be calculated dynamically)
const mentorPlansTemplate = [
  {
    id: 'mentor-basic', name: 'Mentor Basic',
    features: { 
      teachingTools: true, 
      studentManagement: true, 
      aiFeedback: true, 
      styleTransfer: '10/month', 
      storyboarding: false, 
      coLearningSpace: false 
    }
  },
  {
    id: 'mentor-pro', name: 'Mentor Pro',
    features: { 
      teachingTools: true, 
      studentManagement: true, 
      aiFeedback: true, 
      styleTransfer: '25/month', 
      storyboarding: true, 
      coLearningSpace: true 
    }
  },
  {
    id: 'mentor-premium', name: 'Mentor Premium',
    features: { 
      teachingTools: true, 
      studentManagement: true, 
      aiFeedback: true, 
      styleTransfer: 'Unlimited', 
      storyboarding: true, 
      coLearningSpace: true, 
      prioritySupport: true 
    }
  }
];

// Function to generate mentor plans with dynamic pricing
const generateMentorPlans = (mentorCount, isSingleMentorUser) => {
  return mentorPlansTemplate.map(plan => {
    let price;
    
    if (isSingleMentorUser) {
      // Same pricing as organization plans for single mentors
      if (plan.id === 'mentor-basic') price = 999;
      else if (plan.id === 'mentor-pro') price = 1299;
      else if (plan.id === 'mentor-premium') price = 1599;
    } else {
      // Dynamic pricing for organizations - different base rates for each plan
      let basePerMentor;
      if (plan.id === 'mentor-basic') {
        basePerMentor = mentorCount === 1 ? 999 : 
                       mentorCount === 2 ? 899 : 
                       mentorCount === 3 ? 799 : 
                       mentorCount === 4 ? 749 : 699;
      } else if (plan.id === 'mentor-pro') {
        basePerMentor = mentorCount === 1 ? 1299 : 
                       mentorCount === 2 ? 1199 : 
                       mentorCount === 3 ? 1099 : 
                       mentorCount === 4 ? 1049 : 999;
      } else if (plan.id === 'mentor-premium') {
        basePerMentor = mentorCount === 1 ? 1599 : 
                       mentorCount === 2 ? 1499 : 
                       mentorCount === 3 ? 1399 : 
                       mentorCount === 4 ? 1349 : 1299;
      }
      price = basePerMentor * mentorCount;
    }
    
    return {
      ...plan,
      price: `₹${price}/mo`
    };
  });
};

// Display-only membership tiers (Student/Parent) — no wiring to billing yet
// Removed separate Student/Parent cards per new requirement

export default function Membership() {
  const { user, refreshMembership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for tracking if account is new (from backend check)
  const [isNewAccountFromBackend, setIsNewAccountFromBackend] = useState(false);
  const [hasMembershipHistory, setHasMembershipHistory] = useState(false);
  
  // Check if this is a renewal flow
  // IMPORTANT: Backend check result takes precedence over URL state
  // If backend says it's a new account, NEVER show renewal flow, regardless of URL state
  const isRenewalFromState = location.state?.isRenewal;
  
  // Use backend check result: if backend says it's a new account, NEVER show renewal flow
  // Only show renewal if: state explicitly says renewal AND backend doesn't say it's a new account
  // Backend check result takes absolute precedence
  const isRenewal = isRenewalFromState === true && !isNewAccountFromBackend; // Only true if explicitly set to true AND not a new account
  const renewalUserData = location.state?.userData || null;
  // New account if: backend confirms it's new (takes precedence), OR explicitly set to false, OR undefined (default to new account flow)
  const isNewAccount = isNewAccountFromBackend || isRenewalFromState === false || (isRenewalFromState === undefined && !location.state?.userData);

  // State passed when coming from Manage Subscription modal
  const fromManageModal = location.state?.fromManageModal || false;
  const manageSelectedPlan = location.state?.selectedPlan || null;
  const manageAutoCheckout = location.state?.autoCheckout || false;

  // Check if this is a mentor renewal flow
  const isMentorRenewal = new URLSearchParams(location.search).get('renewal') === 'true';
  const ownerId = new URLSearchParams(location.search).get('ownerId');
  
  // Check if this is an organization renewal flow
  const isOrganizationRenewal = location.state?.isOrganizationRenewal || false;
  
  // Check if this is adding mentors to existing plan
  const isAddMentorToExisting = location.state?.isAddMentorToExisting || false;
  const addMentorData = location.state || {};
  
  const [showMentorSelection, setShowMentorSelection] = useState(isMentorRenewal || isOrganizationRenewal || isAddMentorToExisting);
  
  // Initialize selectedPlan based on source (manage modal / renewal / default)
  const [selectedPlan, setSelectedPlan] = useState(() => {
    if (manageSelectedPlan) {
      return manageSelectedPlan;
    }
    // Default to mentor-basic for organization renewals
    if (isOrganizationRenewal || isMentorRenewal) {
      return 'mentor-basic';
    }
    return 'monthly';
  });
  
  // Billing scenario support
  const billingScenario = location.state?.billingScenario;
  const [showBillingSummary, setShowBillingSummary] = useState(!!billingScenario);
  
  // Get mentor count from signup state
  const signupMentorCount = location.state?.mentorCount;
  const [selectedMentors, setSelectedMentors] = useState([]);
  const [existingMentors, setExistingMentors] = useState([]);
  const [mentorCount, setMentorCount] = useState(() => {
    try { const s = localStorage.getItem('signup_mentorCount'); return s ? parseInt(s, 10) : 1; } catch(_) { return 1; }
  });

  // If we arrived from Manage Subscription modal and autoCheckout is requested,
  // immediately open the checkout modal with the selected plan.
  useEffect(() => {
    if (fromManageModal && manageSelectedPlan && manageAutoCheckout) {
      setSelectedPlan(manageSelectedPlan);
      setConfirmMode('paid');
      setConfirmOpen(true);
    }
  }, [fromManageModal, manageSelectedPlan, manageAutoCheckout]);

  // Set mentor count from signup state
  useEffect(() => {
    if (signupMentorCount && signupMentorCount > 0) {
      console.log('Setting mentor count from signup state:', signupMentorCount);
      setMentorCount(signupMentorCount);
      try { localStorage.setItem('signup_mentorCount', String(signupMentorCount)); } catch (_) {}
    }
  }, [signupMentorCount]);
  
  // Organization renewal step management
  const [currentStep, setCurrentStep] = useState(1); // 1, 2, or 3
  const [newMentorEmail, setNewMentorEmail] = useState('');
  const [newMentorName, setNewMentorName] = useState('');
  const [showAddMentor, setShowAddMentor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [trialEligible, setTrialEligible] = useState(true);
  const [membershipStatus, setMembershipStatus] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState('paid');
  const [isCanceled, setIsCanceled] = useState(false);
  const [payMethod, setPayMethod] = useState('card');
  const [successMsg, setSuccessMsg] = useState('');
  const [formError, setFormError] = useState('');
  const [includeUnlimitedStudioAddon, setIncludeUnlimitedStudioAddon] = useState(false);
  const [publicMembershipAddonRate, setPublicMembershipAddonRate] = useState(200);
  // Artmo Code (book owners) state
  const [hasArtmoBook, setHasArtmoBook] = useState(false);
  const [artmoCode, setArtmoCode] = useState('');
  /** Rows of codes in the redeem modal; each valid code extends Base access by 1 calendar month. */
  const [artmoCodeRows, setArtmoCodeRows] = useState(['']);
  const artmoModalWasOpenRef = useRef(false);
  const [billingInterval, setBillingInterval] = useState('monthly'); // monthly | quarterly | semiannual | yearly
  const [showIntervalPicker, setShowIntervalPicker] = useState(false);
  // Incentive discounts for longer terms (totals only)
  const semiannualDiscountPct = 10; // 6-month discount
  const annualDiscountPct = 15; // yearly discount
  const [isMentor, setIsMentor] = useState(() => {
    try {
      const t = localStorage.getItem('signup_accountType') || '';
      return t === 'ngo_admin' || t === 'art_mentor' || t === 'mentor';
    } catch (_) {
      return false;
    }
  });

  // Force mentor count to 1 for single mentor users
  useEffect(() => {
    if (isMentor && user && user.mentorType === 'single') {
      console.log('Forcing mentorCount to 1 for single mentor user');
      setMentorCount(1);
    }
  }, [isMentor, user]);

  // When the Artmo redeem modal opens, seed code rows from the book field; keep row 0 in sync for legacy flows.
  useEffect(() => {
    const isArtmoModal = confirmOpen && confirmMode === 'artmocode';
    if (isArtmoModal && !artmoModalWasOpenRef.current) {
      const t = (artmoCode || '').trim();
      setArtmoCodeRows(t ? [t] : ['']);
    }
    artmoModalWasOpenRef.current = isArtmoModal;
  }, [confirmOpen, confirmMode, artmoCode]);

  useEffect(() => {
    if (!confirmOpen || confirmMode !== 'artmocode') return;
    setArtmoCode(artmoCodeRows[0] ?? '');
  }, [artmoCodeRows, confirmOpen, confirmMode]);

  // Prevent multiple redirects after successful payment
  const [hasRedirected, setHasRedirected] = useState(false);

  // Consent form state for under-13 users
  const [showConsent, setShowConsent] = useState(false);
  const [consent, setConsent] = useState(CONSENT_MAPPING); // Use simplified consent mapping
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  
  // Check if user is under 13 from localStorage (must be defined before useEffect that uses it)
  const isUnder13 = (() => {
    try {
      const stored = localStorage.getItem('signup_isUnder13');
      const result = stored === 'true';
      return result;
    } catch (_) {
      return false;
    }
  })();

  const parentEmailFromSignup = (() => { 
    try { 
      return localStorage.getItem('signup_parentEmail') || ''; 
    } catch(_) { 
      return ''; 
    } 
  })();
  
  // Load consent confirmation status from Firestore when user loads
  const loadConsentStatus = async () => {
    if (!user || !isUnder13) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const confirmed = !!userData.consentConfirmed;
        console.log('🔍 Membership - Consent status loaded:', { confirmed, isUnder13, uid: user.uid });
        setConsentConfirmed(confirmed);
        if (confirmed && userData.consentSettings) {
          setConsent(userData.consentSettings);
        }
      } else {
        console.log('⚠️ Membership - User document does not exist');
        setConsentConfirmed(false);
      }
    } catch (error) {
      console.error('❌ Error loading consent status:', error);
      setConsentConfirmed(false);
    }
  };

  useEffect(() => {
    fetchPublicMembershipConfig()
      .then((c) => {
        if (c && typeof c.premiumAddonMonthlyPrice === 'number') {
          setPublicMembershipAddonRate(c.premiumAddonMonthlyPrice);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadConsentStatus();
    
    // Also reload when page becomes visible (user returns from consent approval)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user && isUnder13) {
        console.log('👁️ Page visible, reloading consent status...');
        loadConsentStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, isUnder13]);

  const pricing = useMemo(() => {
    let base;
    
    // Check if user is single mentor or organization
    const isSingleMentorUser = user?.mentorType === 'single';
    
    if (selectedPlan === 'mentor-basic') {
      if (isSingleMentorUser) {
        base = 999; // Fixed price for single mentor basic (same as plan cards)
      } else {
        // Organization pricing: per mentor calculation for Basic
        const perMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
        base = perMentor * mentorCount;
      }
    } else if (selectedPlan === 'mentor-pro') {
      if (isSingleMentorUser) {
        base = 1299; // Fixed price for single mentor pro (same as plan cards)
      } else {
        // Organization pricing: per mentor calculation for Pro
        const perMentor = mentorCount === 1 ? 1299 : mentorCount === 2 ? 1199 : mentorCount === 3 ? 1099 : mentorCount === 4 ? 1049 : 999;
        base = perMentor * mentorCount;
      }
    } else if (selectedPlan === 'mentor-premium') {
      if (isSingleMentorUser) {
        base = 1599; // Fixed price for single mentor premium (same as plan cards)
      } else {
        // Organization pricing: per mentor calculation for Premium
        const perMentor = mentorCount === 1 ? 1599 : mentorCount === 2 ? 1499 : mentorCount === 3 ? 1399 : mentorCount === 4 ? 1349 : 1299;
        base = perMentor * mentorCount;
      }
    } else {
      // Fallback to old logic for other plans
    const perMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
    const monthlySubtotal = perMentor * mentorCount;
    if (selectedPlan === 'annual') {
      const effPerMentor = Math.round(perMentor * (1 - annualDiscountPct/100));
      const effMonthly = effPerMentor * mentorCount;
        base = effMonthly * 12;
    } else if (selectedPlan === 'semiannual') {
      const effPerMentor = Math.round(perMentor * (1 - semiannualDiscountPct/100));
      const effMonthly = effPerMentor * mentorCount;
        base = effMonthly * 6;
    } else {
      base = monthlySubtotal;
    }
    }
    
    const gst = Math.round(base * 0.18 * 100) / 100;
    const total = Math.round((base + gst) * 100) / 100;
    const suffix = selectedPlan === 'annual' ? '/yr' : selectedPlan === 'semiannual' ? '/6mo' : '/mo';
    
    // Calculate actual per-mentor price
    let actualPerMentor;
    if (user?.mentorType === 'organization') {
      if (selectedPlan === 'mentor-basic') {
        actualPerMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
      } else if (selectedPlan === 'mentor-pro') {
        actualPerMentor = mentorCount === 1 ? 1299 : mentorCount === 2 ? 1199 : mentorCount === 3 ? 1099 : mentorCount === 4 ? 1049 : 999;
      } else if (selectedPlan === 'mentor-premium') {
        actualPerMentor = mentorCount === 1 ? 1599 : mentorCount === 2 ? 1499 : mentorCount === 3 ? 1399 : mentorCount === 4 ? 1349 : 1299;
      } else {
        actualPerMentor = mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
      }
    } else {
      actualPerMentor = base; // For single mentor plans, the base is the per-mentor price
    }
    
    return { base, gst, total, suffix, perMentor: actualPerMentor, monthlySubtotal: base };
  }, [selectedPlan, mentorCount, user?.mentorType]);

  // Effective per-mentor monthly price after plan discount (for mentors)
  const effectivePerMentor = useMemo(() => {
    const base = pricing.perMentor || 0;
    if (selectedPlan === 'annual') return Math.round(base * (1 - annualDiscountPct/100));
    if (selectedPlan === 'semiannual') return Math.round(base * (1 - semiannualDiscountPct/100));
    return base;
  }, [pricing.perMentor, selectedPlan, annualDiscountPct, semiannualDiscountPct]);
  const effectiveMonthlySubtotal = useMemo(() => effectivePerMentor * mentorCount, [effectivePerMentor, mentorCount]);

  // Payment and billing form state (client-side required validation)
  const [cardEmail, setCardEmail] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [upiId, setUpiId] = useState('');
  const [billName, setBillName] = useState('');
  const [billAddr1, setBillAddr1] = useState('');
  const [billAddr2, setBillAddr2] = useState('');
  const [billCity, setBillCity] = useState('');
  const [billState, setBillState] = useState('');
  const [billPostal, setBillPostal] = useState('');
  const [billCountry, setBillCountry] = useState('');

  const formatExpiryInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const isValidCard = () => {
    const digits = cardNumber.replace(/\D/g, '');
    const expOk = /^(0[1-9]|1[0-2])\/(\d{2})$/.test(cardExp.trim());
    return (
      cardEmail.trim().length > 3 &&
      cardName.trim().length > 1 &&
      digits.length >= 12 &&
      expOk &&
      cardCvc.trim().length >= 3
    );
  };

  const isValidUpi = () => upiId.includes('@') && upiId.indexOf('@') > 0;

  const isValidBilling = () => (
    billName.trim() && billAddr1.trim() && billCity.trim() && billState.trim() && billPostal.trim() && billCountry.trim()
  );

  const canPay = confirmMode !== 'paid' ? true : (
    (payMethod === 'card' ? isValidCard() : isValidUpi()) && 
    isValidBilling() && 
    (!isUnder13 || consentConfirmed) // Include consent validation for under-13 users
  );

  // Debug: log mount and key state values when they change
  // useEffect(() => {
  //   console.log('Membership page loaded', { selectedPlan, trialEligible, membershipStatus, isMentor });
  // }, [selectedPlan, trialEligible, membershipStatus, isMentor]);

  // Auto-select first mentor plan when isMentor becomes true
  useEffect(() => {
    if (isMentor && selectedPlan === 'monthly') {
      setSelectedPlan('mentor-basic'); // Default to mentor-basic
    }
  }, [isMentor, selectedPlan]);

  // Show loading state while determining user type - always start with loading for mentor users
  const [isLoadingUserType, setIsLoadingUserType] = useState(true);
  const userDataLoaded = useRef(false);
  const [adminPlans, setAdminPlans] = useState(null); // Plans from admin dashboard

  // Load admin plans from backend API (bypasses Firestore security rules)
  useEffect(() => {
    const loadAdminPlans = async () => {
      try {
        console.log('💾 Membership - Loading admin plans from backend API');
        const res = await fetch(`${backendConfig.baseUrl}/api/admin/membership-plans`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.plans) {
            setAdminPlans(data.plans);
            console.log('✅ Membership - Admin plans loaded from backend:', data.plans);
          } else {
            console.log('ℹ️ Membership - No admin plans found in Firestore, using defaults');
          }
        } else {
          console.warn('⚠️ Membership - Failed to load admin plans, using defaults');
        }
      } catch (error) {
        console.error('❌ Membership - Error loading admin plans:', error);
      }
    };
    loadAdminPlans();
  }, []);

  // Force single mentors to use organization membership flow
  const isSingleMentor = user?.mentorType === 'single';
  const effectiveIsMentor = isMentor || isSingleMentor;
  const effectiveMentorType = user?.mentorType; // Keep original mentorType, don't override
  const effectiveMentorCount = isSingleMentor ? 1 : mentorCount;

  useEffect(() => {
    const load = async () => {
      console.log('🔍 Membership - useEffect triggered:', { user: !!user, userDataLoaded: userDataLoaded.current });
      
      // Safety timeout to ensure loading is always set to false
      const safetyTimeout = setTimeout(() => {
        console.warn('⏱️ Membership - Safety timeout reached, setting loading to false');
        setIsLoadingUserType(false);
      }, 5000); // 5 second safety timeout
      
      if (!user || userDataLoaded.current) {
        // If no user, still set loading to false after a short delay
        if (!user) {
          console.log('🔍 Membership - No user, setting timeout');
          setTimeout(() => {
            clearTimeout(safetyTimeout);
            setIsLoadingUserType(false);
          }, 1000);
        } else {
          clearTimeout(safetyTimeout);
        }
        return;
      }
      
      try {
        userDataLoaded.current = true;
        setIsLoadingUserType(true);
        console.log('🔍 Membership - Using user data from AuthContext:', {
          accountType: user.accountType,
          mentorType: user.mentorType,
          uid: user.uid
        });
        
        // Use the user data from AuthContext instead of doing another Firestore query
        const acctType = (user.accountType || '').toString();
        const shouldBeMentor = acctType === 'ngo_admin' || acctType === 'art_mentor' || acctType === 'mentor';
        console.log('🔍 Membership - User data from AuthContext:', {
          accountType: acctType,
          mentorType: user.mentorType,
          shouldBeMentor,
          currentIsMentor: isMentor,
          userData: user
        });
        if (shouldBeMentor !== isMentor) {
          console.log('🔄 Membership - Updating isMentor from', isMentor, 'to', shouldBeMentor);
          setIsMentor(shouldBeMentor);
        }
        
        // For additional user data, we still need to query Firestore with timeout
        console.log('🔍 Membership - Loading additional user data from Firestore for uid:', user.uid);
        const firestorePromise = getDoc(doc(db, 'users', user.uid));
        const firestoreTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Firestore query timeout')), 3000)
        );
        
        let snap;
        try {
          snap = await Promise.race([firestorePromise, firestoreTimeout]);
        } catch (firestoreError) {
          console.warn('⚠️ Membership - Firestore query failed or timed out:', firestoreError);
          snap = null; // Continue with defaults
        }
        
        if (snap && snap.exists()) {
          const data = snap.data();
          
          // If mentor seats stored on profile, prefer that over localStorage
          const seats = parseInt(data.mentorSeatsPurchased || data?.membership?.mentorSeatsPurchased || 0, 10);
          if (!Number.isNaN(seats) && seats > 0) {
            setMentorCount(seats);
            try { localStorage.setItem('signup_mentorCount', String(seats)); } catch (_) {}
          }
          const m = data.membership || {};
          const used = !!m.trialUsed;
          const status = m.status || '';
          
          // Check if this is a new account (no membership data at all)
          const hasNoMembership = !m.status && !m.tier && !m.planId;
          
              // Also check backend API to confirm if it's a new account with timeout
              try {
                const abortController = new AbortController();
                const apiTimeout = setTimeout(() => abortController.abort(), 3000); // 3 second timeout
                
                const statusRes = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}&_t=${Date.now()}`, {
                  signal: abortController.signal
                });
                clearTimeout(apiTimeout);
                
                if (statusRes.ok) {
                  const statusData = await statusRes.json();
                  const isNewAccountFromBackend = statusData.isNewAccount === true;
                  const membershipStatusFromBackend = statusData.membership?.status || '';
                  const membershipAllowed = statusData.allowed === true;
                  
                  console.log('🔍 Membership - Backend status check:', {
                    isNewAccount: isNewAccountFromBackend,
                    status: membershipStatusFromBackend,
                    allowed: membershipAllowed,
                    hasNoMembership,
                    isRenewalFromState: location.state?.isRenewal,
                    fullStatusData: statusData
                  });
                  
                  // Determine if this is truly a new account (never had membership) vs expired
                  // New account = no membership status, no tier, no planId, and backend says isNewAccount
                  // OR: membership exists but is completely empty (no status, tier, planId, trialUsed, dates)
                  const membershipIsEmpty = !statusData.membership?.status && 
                                           !statusData.membership?.tier && 
                                           !statusData.membership?.planId &&
                                           !statusData.membership?.trialUsed &&
                                           !statusData.membership?.activatedAt &&
                                           !statusData.membership?.currentPeriodEnd &&
                                           !statusData.membership?.expiresAt &&
                                           !statusData.membership?.trialStartsAt &&
                                           !statusData.membership?.trialEndsAt;
                  
                  // If membership is empty AND not explicitly expired, treat as new account
                  // Also check Firestore data for additional confirmation
                  const firestoreIsEmpty = hasNoMembership && !used && !status;
                  const hasHistory = !membershipIsEmpty || !firestoreIsEmpty;
                  const isTrulyNewAccount = !hasHistory && trialEligible;
                  
                  console.log('🔍 Membership - Account type determination:', {
                    isNewAccountFromBackend,
                    membershipIsEmpty,
                    isTrulyNewAccount,
                    membershipStatus: statusData.membership?.status,
                    isExpired: statusData.membership?.isExpired,
                    hasTrialUsed: statusData.membership?.trialUsed
                  });
                  
                  // Store the backend check result - this will trigger a re-render
                  setIsNewAccountFromBackend(isTrulyNewAccount);
                  setHasMembershipHistory(hasHistory);
                  
                  // If backend confirms it's a new account, ensure renewal flow is not shown
                  if (isTrulyNewAccount) {
                    console.log('🆕 Membership page - Confirmed new account (never had membership), ensuring renewal flow is not shown');
                  } else if (membershipStatusFromBackend === 'expired' || statusData.membership?.isExpired) {
                    console.log('🔄 Membership page - Backend confirmed expired account, should show renewal flow');
                    // If expired, ensure renewal flow is shown (unless explicitly marked as new)
                    if (location.state?.isRenewal === undefined) {
                      // Update URL state to indicate renewal if not already set
                      console.log('🔄 Setting renewal state based on backend check');
                    }
                  }
                }
              } catch (statusError) {
                if (statusError.name === 'AbortError') {
                  console.warn('⏱️ Membership - Backend status check timed out, using defaults');
                } else {
                  console.warn('⚠️ Membership - Could not check backend status:', statusError);
                }
                // Continue with defaults if API fails
              }
          
          // User is not eligible for trial if:
          // 1. Trial was already used, OR
          // 2. They have an active or trialing membership
          const hasActiveMembership = status === 'active' || status === 'trialing';
          setTrialEligible(!used && !hasActiveMembership);
          setMembershipStatus(status);
          setIsCanceled(!!m.canceled);
          let e = m.expiresAt || m.currentPeriodEnd || m.trialEndsAt || data.subscriptionEnds;
          if (e) e = typeof e.toDate === 'function' ? e.toDate() : new Date(e);
          setExpiresAt(e || null);
        }
      } catch (e) {
        console.error('❌ Membership - Error loading user data:', e);
        // Continue with defaults
      } finally {
        clearTimeout(safetyTimeout);
        // Always set loading to false
        setIsLoadingUserType(false);
        console.log('✅ Membership - Loading complete, plans should be visible');
      }
    };
    load();
  }, [user?.uid]); // Only depend on user.uid, not the entire user object

  // Fetch existing mentors for organization renewal
  useEffect(() => {
    const fetchExistingMentors = async () => {
      if (isOrganizationRenewal && user) {
        try {
          const response = await fetch(`${backendConfig.baseUrl}/api/mentors/list?ownerId=${user.uid}`);
          if (response.ok) {
            const data = await response.json();
            setExistingMentors(data.mentors || []);
            // Set mentor count based on existing mentors
            if (data.mentors && data.mentors.length > 0) {
              setMentorCount(data.mentors.length);
              setSelectedMentors(data.mentors.map(mentor => mentor.id));
            }
          }
        } catch (error) {
          console.error('Error fetching existing mentors:', error);
        }
      }
    };
    fetchExistingMentors();
  }, [isOrganizationRenewal, user]);

  // Add new mentor function
  const addNewMentor = async () => {
    if (!newMentorEmail || !newMentorName) {
      setError('Please fill in all fields');
      return;
    }

    try {
      // Use add-pending endpoint for organization renewal (no email sent yet)
      const endpoint = isOrganizationRenewal ? 'add-pending' : 'invite';
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId: user.uid,
          mentorEmail: newMentorEmail,
          mentorName: newMentorName
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          // Add to existing mentors list
          const newMentor = {
            id: `${newMentorEmail}_mentor`,
            email: newMentorEmail,
            name: newMentorName,
            status: isOrganizationRenewal ? 'pending_payment' : 'pending'
          };
          setExistingMentors([...existingMentors, newMentor]);
          setSelectedMentors([...selectedMentors, newMentor.id]);
          setNewMentorEmail('');
          setNewMentorName('');
          setShowAddMentor(false);
          setMentorCount(selectedMentors.length + 1);
        } else {
          setError(data.error || 'Failed to add mentor');
        }
      } else {
        setError('Failed to add mentor');
      }
    } catch (error) {
      console.error('Error adding mentor:', error);
      setError('Failed to add mentor');
    }
  };

  const handleCheckout = async () => {
    // Try to get user from useAuth hook, fallback to auth.currentUser
    const currentUser = user || auth.currentUser;
    
    if (!currentUser) {
      console.error('❌ handleCheckout: No user found, cannot proceed', { 
        userFromHook: user, 
        currentUserFromAuth: auth.currentUser 
      });
      setError('You must be signed in to continue. Please sign in and try again.');
      setFormError('You must be signed in to continue. Please sign in and try again.');
      setBusy(false);
      return;
    }
    
    // Use currentUser for the rest of the function
    const effectiveUser = currentUser;
    
    // Snapshot current entitlement so we don't show a false success on timeout.
    const preCheckoutStatus = membershipStatus;
    const preCheckoutEndMs = expiresAt instanceof Date ? expiresAt.getTime() : null;

    // Prevent duplicate checkout calls
    if (busy) {
      console.log('⚠️ Checkout already in progress, ignoring duplicate call');
      return;
    }
    
    // Check consent for under-13 users before proceeding
    const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
    const selectedPlanData = plans.find(p => p.id === selectedPlan);
    // Artmo Code redemption should be driven by the confirm modal mode,
    // not only by the currently selected plan card.
    const isArtmoCode =
      confirmMode === 'artmocode' ||
      (selectedPlanData && selectedPlanData.id === 'artmocode' && selectedPlanData.requiresPayment === false);
    const isFreeTrial = selectedPlanData && selectedPlanData.id === 'free-trial' && selectedPlanData.requiresPayment === false;
    
    if (isUnder13 && !consentConfirmed) {
      if (isArtmoCode) {
        setFormError('Please check your guardian email and provide consent to use Artmosphere features. Once consent is provided, you can redeem your Artmo Code for Base access (1 month per code).');
      } else {
        setFormError('Parent consent is required for children under 13. Please complete the consent form first.');
      }
      setBusy(false);
      return;
    }
    
    setBusy(true); 
    setError('');
    setFormError('');
    // Hard UI safety: if backend request never resolves/aborts correctly,
    // the modal can get stuck on "Processing payment…".
    // Keep this > the network abort timeout below.
    const UI_SAFETY_MS = 150000;
    const safetyTimeoutId = setTimeout(() => {
      console.warn('⏱️ [Membership] checkout UI safety timeout fired; clearing busy overlay.');
      setError('Payment is taking longer than expected. Please try again.');
      setFormError('Payment timed out. Please try again in a moment.');
      setConfirmOpen(false);
      setBusy(false);
    }, UI_SAFETY_MS);
    try {
      console.log('🚀 Starting checkout process...', { user: effectiveUser.uid, selectedPlan, isFreeTrial });
      
      if (isArtmoCode) {
        console.log('🎁 Artmo Code — batch redeem (1 month per valid code)');
        const storedCode = (() => {
          try { return localStorage.getItem('artmoCode') || ''; } catch (_) { return ''; }
        })();
        const fromRows = artmoCodeRows.map((c) => String(c || '').trim()).filter(Boolean);
        const codes =
          fromRows.length > 0
            ? fromRows
            : (artmoCode || storedCode || '').trim()
              ? [(artmoCode || storedCode || '').trim()]
              : [];

        if (!codes.length) {
          setFormError('Enter at least one Artmo code from your book.');
          setBusy(false);
          return;
        }

        let data;
        try {
          data = await redeemArtmoCodes(codes);
        } catch (err) {
          console.error('❌ Artmo code redeem failed:', err);
          setFormError(err?.message || 'Could not redeem codes. Please try again.');
          setBusy(false);
          return;
        }

        const redeemedOk =
          (Array.isArray(data?.validCodes) && data.validCodes.length > 0) ||
          (typeof data?.monthsGranted === 'number' && data.monthsGranted > 0);
        if (!redeemedOk) {
          const results = Array.isArray(data?.results) ? data.results : [];
          const invalid = results.filter((r) => r && r.valid === false);
          const allNotFound = invalid.length > 0 && invalid.every((r) => r.reason === 'not_found');
          const reasons = invalid
            .map((r) => `${r.code}: ${r.reason || 'invalid'}`)
            .join('; ');
          setFormError(
            allNotFound
              ? 'We could not find that code in our system. Double-check the characters, or contact support with your book details.'
              : reasons
                ? `No codes could be redeemed. ${reasons}`
                : 'No valid codes could be redeemed. Check your codes and try again.'
          );
          setBusy(false);
          return;
        }

        try {
          localStorage.setItem('hasArtmoBook', 'true');
          localStorage.setItem('artmoCode', codes[0]);
        } catch (_) {}

        try {
          await Promise.race([
            refreshMembership(effectiveUser.uid, { force: true }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('membership_refresh_timeout')), 5000)),
          ]);
        } catch (_) {}

        setConfirmOpen(false);
        setBusy(false);

        try {
          const now = Date.now();
          localStorage.setItem('membershipJustActivatedAt', String(now));
          localStorage.setItem('membershipJustActivatedPlan', selectedPlan);
        } catch (_) {}

        const granted = data.monthsGranted ?? data.validCodes?.length ?? 0;
        navigate('/onboarding/artmo-success', {
          replace: true,
          state: {
            monthsGranted: granted,
            periodEnd: data.periodEnd,
          },
        });
        return;
      }

      // Legacy (old deployments) free-trial support. This should not appear in UI anymore.
      if (isFreeTrial) {
        console.log('🆓 Legacy free-trial selected - activating via checkout endpoint');
        const trialData = {
          uid: effectiveUser.uid,
          userId: effectiveUser.uid,
          plan: 'basic',
          mentorSeats: mentorCount,
          isRenewal: !!effectiveIsRenewal || !!isMentorRenewal || !!isOrganizationRenewal,
          skipPayment: true
        };

        const trialController = new AbortController();
        const trialTimeoutId = setTimeout(() => {
          console.log('⏰ Free trial request timed out after 20 seconds');
          trialController.abort();
        }, 20000);

        let res;
        try {
          res = await authFetch(`${backendConfig.baseUrl}/api/membership/checkout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(trialData),
            signal: trialController.signal
          });
        } finally {
          clearTimeout(trialTimeoutId);
        }

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Trial activation failed');
        }

        setConfirmOpen(false);
        setBusy(false);

        try { localStorage.setItem('membershipJustActivatedAt', String(Date.now())); } catch (_) {}
        try { localStorage.setItem('membershipJustActivatedPlan', selectedPlan); } catch (_) {}

        return;
      }
      
      // Regular checkout for paid plans
      const checkoutData = { 
        uid: effectiveUser.uid, 
        userId: effectiveUser.uid, 
        plan: selectedPlan, 
        mentorSeats: mentorCount, 
        hasArtmoBook: !!hasArtmoBook, 
        artmoCode: (artmoCode||'').trim(),
        // New fields for Base/Pro + duration (student/parent plans)
        planTier: !effectiveIsMentor ? 'base' : undefined,
        includePremiumAddon:
          !effectiveIsMentor && selectedPlan === 'basic' && !!includeUnlimitedStudioAddon,
        billingInterval: !effectiveIsMentor ? billingInterval : undefined,
        // Pass isRenewal flag to prevent free trial for renewals
        isRenewal: !!effectiveIsRenewal || !!isMentorRenewal || !!isOrganizationRenewal
      };
      
      // Add billing scenario data if present
      if (billingScenario) {
        checkoutData.billingScenario = billingScenario;
        checkoutData.scenarioType = billingScenario.type;
      }
      
      console.log('🔄 Checkout data - isRenewal:', checkoutData.isRenewal, {
        isRenewal,
        isMentorRenewal,
        isOrganizationRenewal
      });
      
      console.log('📤 Sending checkout request:', checkoutData);
      
      // Quick health check before checkout (non-blocking)
      try {
        const healthCheck = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${effectiveUser.uid}`, {
          method: 'GET',
          signal: AbortSignal.timeout(3000) // 3 second timeout for health check
        });
        if (!healthCheck.ok) {
          console.warn('⚠️ Backend health check failed, but proceeding with checkout');
        }
      } catch (healthError) {
        console.warn('⚠️ Backend health check error:', healthError.message);
        // Continue anyway - might be a temporary issue
      }
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ Checkout request timed out after 120 seconds');
        controller.abort();
      }, 120000); // 120 second timeout
      
      console.log('📤 Making fetch request to:', `${backendConfig.baseUrl}/api/membership/checkout`);
      const res = await authFetch(`${backendConfig.baseUrl}/api/membership/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutData),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('📥 Checkout response received, status:', res.status);
      console.log('📥 Checkout response headers:', Object.fromEntries(res.headers.entries()));
      const data = await res.json();
      console.log('📥 Checkout response data:', data);
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      
      // Send emails via Cloud Functions (NON-BLOCKING).
      // Payment success UX should not wait on email delivery / consent reads.
      try {
        const functionsBase =
          process.env.REACT_APP_FUNCTIONS_BASE || 'https://us-central1-artmosphere-89b64.cloudfunctions.net';
        console.log('📧 Using functions base:', functionsBase);

        // Get consent from state or from Firestore if already saved (with timeout)
        let consentToSend = consent;
        if (!consentToSend || Object.keys(consentToSend).length === 0) {
          try {
            const consentTimeoutMs = 4000;
            const consentTimeout = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Consent read timed out')), consentTimeoutMs);
            });
            const consentDoc = await Promise.race([getDoc(doc(db, 'users', effectiveUser.uid)), consentTimeout]);
            if (consentDoc?.exists?.() && consentDoc.data?.()?.consent) {
              consentToSend = consentDoc.data().consent;
              console.log('📧 Using consent from Firestore:', consentToSend);
            }
          } catch (e) {
            console.log('⚠️ Could not fetch consent from Firestore:', e.message);
          }
        }

        // Fire-and-forget email request with timeout (do not await)
        const emailController = new AbortController();
        const emailTimeoutId = setTimeout(() => {
          console.log('⏰ Email request timed out after 15 seconds');
          emailController.abort();
        }, 15000);

        console.log('📧 Sending email request with consent (non-blocking)...');
        fetch(`${functionsBase}/sendSubscriptionEmails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: effectiveUser.uid,
            plan: selectedPlan,
            consent: consentToSend,
            artmoCode: (artmoCode || '').trim(),
            hasArtmoBook: !!hasArtmoBook,
          }),
          signal: emailController.signal,
        })
          .then(async (resp) => {
            console.log('📧 Email response status:', resp.status);
            try {
              const result = await resp.json();
              console.log('📧 sendSubscriptionEmails result:', { status: resp.status, result });
            } catch {
              // Ignore non-JSON bodies
            }
          })
          .catch((e) => {
            console.error('❌ sendSubscriptionEmails error:', e.message);
          })
          .finally(() => {
            clearTimeout(emailTimeoutId);
          });
      } catch (e) {
        console.error('❌ sendSubscriptionEmails outer error:', e.message);
      }

      // Send pending mentor invitations after successful payment (DISABLED FOR TESTING)
      if (isOrganizationRenewal) {
        console.log('📧 Mentor invitation emails DISABLED for testing - skipping mentor invitation triggers');
        // try {
        //   console.log('📧 Sending mentor invitation request...');
        //   const mentorResponse = await fetch(`${backendConfig.baseUrl}/api/mentors/send-pending-invites`, {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify({ ownerId: user.uid })
        //   });
        //   console.log('📧 Mentor invitation response status:', mentorResponse.status);
        //   if (mentorResponse.ok) {
        //     const mentorData = await mentorResponse.json();
        //     console.log('📧 Mentor invitations sent:', mentorData);
        //   } else {
        //     console.error('❌ Failed to send mentor invitations');
        //   }
        // } catch (e) {
        //   console.error('❌ Error sending mentor invitations:', e.message);
        //   console.log('⚠️ Continuing despite mentor invitation error...');
        // }
      }
      
      // Set grace period for ProtectedRoute to avoid redirect loop
      try { 
        const now = Date.now();
        localStorage.setItem('membershipJustActivatedAt', String(now)); 
        localStorage.setItem('membershipJustActivatedPlan', selectedPlan);
        console.log('✅ Set membership grace period and plan:', { now, selectedPlan });
      } catch (_) {}
      
      // Show success message, then automatically navigate to the next step
      console.log('✅ Setting success message...');
      setSuccessMsg('✅ Subscription successful! Your Artmosphere subscription is now active.');
      setBusy(false); // Make sure to stop the busy state
      console.log('✅ Success message set, starting navigation timeout...');
      console.log('🎯 Checkout process completed successfully!');
      
      // Use longer timeout to ensure grace period is fully set and success message is visible
      setTimeout(async () => {
        try {
          // Prevent multiple redirects
          if (hasRedirected) {
            console.log('⚠️ Redirect already attempted, skipping...');
            return;
          }
          
          // Check if user is already on the target page
          const currentPath = window.location.pathname;
          if (currentPath === '/mentor-dashboard' || currentPath === '/organization-dashboard' || currentPath === '/my-art-world' || currentPath === '/artist-wall') {
            console.log('⚠️ User already on target page:', currentPath, 'skipping redirect...');
            return;
          }
          
          console.log('✅ Navigation timeout triggered, checking user type...');
          console.log('🔍 Navigation debug:', {
            isMentor,
            mentorType: effectiveUser?.mentorType,
            isOrganizationRenewal,
            mentorCount,
            effectiveIsMentor,
            isSingleMentor,
            user: effectiveUser ? { uid: effectiveUser.uid, email: effectiveUser.email, accountType: effectiveUser.accountType, mentorType: effectiveUser.mentorType } : null
          });
          setHasRedirected(true); // Set flag to prevent multiple redirects
          
          console.log('🎯 NAVIGATION DECISION POINT:');
          console.log('  - effectiveIsMentor:', effectiveIsMentor);
          console.log('  - isSingleMentor:', isSingleMentor);
          console.log('  - user.mentorType:', effectiveUser?.mentorType);
          console.log('  - isOrganizationRenewal:', isOrganizationRenewal);
          
          if (effectiveIsMentor) {
            // Check if this is a single mentor
            if (isSingleMentor) {
              // Single mentor: go to mentor dashboard
              console.log('✅ SINGLE MENTOR DETECTED - Redirecting to mentor dashboard');
              console.log('🚀 Executing navigate("/mentor-dashboard", { replace: true })');
              navigate('/mentor-dashboard', { replace: true });
              
              // Add a check to see if navigation worked
              setTimeout(() => {
                console.log('🔍 Post-navigation check - Current path:', window.location.pathname);
                if (window.location.pathname !== '/mentor-dashboard') {
                  console.error('❌ NAVIGATION FAILED! Still on:', window.location.pathname);
                  console.log('🔄 Attempting force redirect...');
                  window.location.href = '/mentor-dashboard';
          } else {
                  console.log('✅ Navigation successful - Now on mentor dashboard');
                }
              }, 100);
            } else if (effectiveUser?.mentorType === 'organization' && !isOrganizationRenewal) {
              // New organization: redirect to mentor setup for n mentors
              console.log('New organization detected, redirecting to mentor setup for', mentorCount, 'mentors');
              navigate('/mentor-setup', { 
                state: { 
                  isNewOrganization: true,
                  mentorCount: mentorCount,
                  userData: { uid: effectiveUser.uid, email: effectiveUser.email }
                },
                replace: true // Use replace to avoid back button issues
              });
            } else if (isOrganizationRenewal) {
              // Existing organization renewal: go to dashboard
              console.log('Organization renewal, redirecting to dashboard');
              navigate('/organization-dashboard', { replace: true });
            } else {
              // Fallback: go to mentor dashboard
              console.log('Mentor user detected, redirecting to mentor dashboard');
              navigate('/mentor-dashboard', { replace: true });
            }
          } else {
            // Student users - check if profile exists, then redirect accordingly
            console.log('Non-mentor user detected, checking for artist profile...');
            try {
              await refreshMembership(effectiveUser.uid, { force: true });
            } catch (_) {
              /* non-blocking; ProtectedRoute also trusts Firestore */
            }
            const profileSnap = await getDoc(doc(db, 'artistProfiles', effectiveUser.uid));
            if (profileSnap.exists()) {
              console.log('Student with profile, redirecting to artist wall');
              navigate('/my-art-world', { replace: true });
            } else {
              console.log('Student without profile, redirecting to profile creation');
              navigate('/artist-profile', { replace: true, state: { paymentSuccess: true } });
            }
          }
        } catch (_) {
          // Fallback navigation
          if (effectiveIsMentor) {
            if (isSingleMentor) {
              navigate('/mentor-dashboard', { replace: true });
            } else if (effectiveUser?.mentorType === 'organization' && !isOrganizationRenewal) {
              navigate('/mentor-setup', { 
                state: { 
                  isNewOrganization: true,
                  mentorCount: mentorCount,
                  userData: { uid: effectiveUser.uid, email: effectiveUser.email }
                },
                replace: true
              });
            } else if (isOrganizationRenewal) {
              navigate('/organization-dashboard', { replace: true });
            } else {
              navigate('/mentor-dashboard', { replace: true });
            }
          } else {
            // For students without profile, redirect directly to artist profile creation page (Welcome, Future Artist!)
            navigate('/artist-profile', { replace: true, state: { paymentSuccess: true } });
          }
        } finally {
          setSuccessMsg('');
          setConfirmOpen(false);
        }
      }, 1400);
    } catch (e) {
      console.error('❌ Checkout error:', e);
      console.error('❌ Error details:', e.message, e.stack);
      
      let errorMessage = e.message;
      const isAbort = e.name === 'AbortError' || e.message?.includes('aborted');

      if (isAbort) {
        errorMessage = 'Payment request timed out. The backend server may not be responding. Please check if the backend is running and try again.';

        // Best-effort: check if membership actually became active despite timeout
        try {
          console.log('🔍 Verifying membership status after timeout...');
          const userDoc = await getDoc(doc(db, 'users', effectiveUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data() || {};
            const membership = data.membership || {};
            let expiresAt = membership.expiresAt || membership.currentPeriodEnd || membership.trialEndsAt;
            if (expiresAt && typeof expiresAt.toDate === 'function') expiresAt = expiresAt.toDate();
            const notExpired = expiresAt ? expiresAt.getTime() > Date.now() : false;
            const isActive = (membership.status === 'active' || membership.status === 'trialing') && notExpired;

            const preWasActive = preCheckoutStatus === 'active' || preCheckoutStatus === 'trialing';
            const endAdvanced =
              typeof preCheckoutEndMs === 'number' &&
              expiresAt &&
              typeof expiresAt.getTime === 'function' &&
              expiresAt.getTime() > preCheckoutEndMs + 60_000; // require real period extension

            // Only treat as success if:
            // - user wasn't already active before the click, OR
            // - their entitlement period end moved forward.
            if (isActive && (!preWasActive || endAdvanced)) {
              console.log('✅ Membership is active after timeout - treating checkout as successful');
              setError('');
              setFormError('');
              setSuccessMsg('✅ Subscription successful! Your Artmosphere subscription is now active.');
              
              // Redirect student renewals to My Art World; mentors/org handled by ProtectedRoute
              navigate('/my-art-world', { replace: true });
              setConfirmOpen(false);
              return;
            }
          }
        } catch (statusErr) {
          console.warn('⚠️ Could not verify membership status after timeout:', statusErr?.message);
        }
      } else if (e.message === 'Load failed' || e.message?.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to the payment server. Please check if the backend server is running on port 3001 and try again.';
      } else if (e.message?.includes('timeout')) {
        errorMessage = 'Payment request timed out. Please check your connection and try again.';
      }
      
      // Set both error and formError so it displays in modal and outside
      setError(errorMessage);
      setFormError(errorMessage);
      setConfirmOpen(false); // Close the modal so user can see the error
    } finally {
      clearTimeout(safetyTimeoutId);
      console.log('🔄 Checkout process finished, setting busy to false');
      setBusy(false);
    }
  };

  // Consent form functions
  const handleConsentAgree = async (consentData) => {
    setConsentSubmitting(true);
    try {
      console.log('🔍 Confirming consent:', { consentData, isUnder13, parentEmailFromSignup });
      const userId = user?.uid || 'current-user';
      await saveConsentSettings({ 
        userId, 
        accountType: isUnder13 ? 'student' : 'parent', 
        consent: consentData, 
        parentEmail: parentEmailFromSignup || null,
        consentConfirmed: true
      });
      setConsent(consentData);
      setConsentConfirmed(true);
      setShowConsent(false);
      console.log('✅ Consent confirmed and saved');
    } catch (error) {
      console.error('❌ Error saving consent:', error);
      setError('Failed to save consent. Please try again.');
    } finally {
      setConsentSubmitting(false);
    }
  };

  const handlePortal = async () => {
    if (!user) return;
    setBusy(true); setError('');
    try {
      const action = isCanceled ? 'resume' : 'cancel';
      const res = await authFetch(`${backendConfig.baseUrl}/api/membership/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, action })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Portal failed');
      // Refresh server status after cancel/resume
      const statusRes = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`);
      if (statusRes.ok) {
        const j = await statusRes.json();
        const m = j.membership || {};
        const status = m.status || '';
        // User is not eligible for trial if:
        // 1. Trial was already used, OR
        // 2. They have an active or trialing membership
        const hasActiveMembership = status === 'active' || status === 'trialing';
        setMembershipStatus(status);
        setIsCanceled(!!m.canceled || status === 'canceled');
        setTrialEligible(!m.trialUsed && !hasActiveMembership);
        let e = j.expiresAt;
        if (e) e = new Date(e);
        setExpiresAt(e || null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAddMentorCheckout = async () => {
    if (!user || !addMentorData) return;
    
    setBusy(true);
    setError('');
    
    try {
      console.log('🚀 Starting add mentor checkout process...');
      console.log('📤 Sending add mentor request:', {
        uid: user.uid,
        additionalSeats: addMentorData.mentorCount,
        planType: addMentorData.planType
      });

      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/add-mentors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          additionalSeats: addMentorData.mentorCount,
          planType: addMentorData.planType
        })
      });

      console.log('📥 Add mentor response status:', response.status);
      const result = await response.json();
      console.log('📥 Add mentor response data:', result);

      if (result.ok) {
        setSuccessMsg(`✅ ${result.billing.confirmation}`);
        
        // Navigate back to organization dashboard after a short delay
        setTimeout(() => {
          navigate('/organization-dashboard');
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to add mentors');
      }
    } catch (error) {
      console.error('❌ Add mentor error:', error);
      console.error('❌ Error details:', error.message);
      setError(error.message || 'Failed to add mentors. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // Recalculate isRenewal and isNewAccount based on current state.
  // Treat any existing membership history (even if expired) as a renewal journey.
  const effectiveIsRenewal =
    hasMembershipHistory &&
    (isRenewalFromState === true || isRenewalFromState === undefined);

  // Free trial is removed. New accounts choose: Artmo (books) or Artmosphere Studio subscription (+ optional unlimited add-on).
  const effectiveIsNewAccount = !hasMembershipHistory;
  
  const showTrialCta = false;
  const trialCtaText = 'Redeem Artmo Code';
  const subscribeCtaText = effectiveIsRenewal || isMentorRenewal ? 'Renew Your Plan' : 'Select Plan';
  const headerCopy = isMentorRenewal
    ? 'Your organization membership was cancelled and has expired. Select mentors and choose a plan to restore access.'
    : isOrganizationRenewal
    ? 'Your organization membership has expired. Confirm your mentors and choose a plan to restore access.'
    : effectiveIsRenewal 
    ? 'Welcome back! Your membership was cancelled and has expired. Choose a plan to renew and continue your Artmosphere journey.'
    : effectiveIsNewAccount
    ? 'Choose free Studio access with your book code, or subscribe to Artmosphere Studio (add unlimited access at checkout if you like).'
    : 'Choose a plan to continue your membership.';
  
  const openArtmoRedeemModal = () => {
    setIncludeUnlimitedStudioAddon(false);
    setSelectedPlan('artmocode');
    setShowIntervalPicker(false);
    setConfirmMode('artmocode');
    setConfirmOpen(true);
  };

  return (
    <React.Fragment>
      <div className="art-premium-page-bg artm-page artm-page-pad min-h-screen px-4 py-8 md:py-10 flex justify-center">
        {successMsg && <KidBackground variant="confetti" intensity={1.2} />}
        <div className="art-premium-surface art-premium-surface--sheet relative z-10 w-full max-w-5xl">
          <div className="art-premium-surface__gold-bar" aria-hidden />
          <header className="relative shrink-0 border-b border-[#e8d4bc]/60 bg-[#fffdf9]/95 px-5 py-4 backdrop-blur-md sm:px-8 sm:py-5">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-12 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,214,140,0.35),transparent_70%)]"
              aria-hidden
            />
            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="art-premium-eyebrow mb-1">Artmosphere Studio</p>
                <h1 className="font-serif text-2xl font-semibold leading-tight text-[#2f251b] sm:text-3xl md:text-4xl">
                  {isMentorRenewal ? 'Restore Organization Access' : 
                   isOrganizationRenewal ? 'Renew Organization Membership' : 
                   effectiveIsRenewal ? 'Renew Your Plan' : 
                   isMentor ? 'Choose Your Mentor Plan' : 'Choose Your Creative Plan'}
                </h1>
              </div>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigate('/home', { replace: true });
                  } catch (e) {
                    window.location.href = '/home';
                  }
                }}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e8c97a] bg-[#fff9ef] text-[#5c4428] shadow-sm transition hover:border-[#f5cb5c] hover:bg-white hover:shadow-md"
                aria-label="Close and return to home"
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
            <p className="relative mt-3 text-center text-sm leading-relaxed text-[#5a4632] sm:text-left sm:text-base max-w-3xl">
            {isMentorRenewal ? 'Restore access to your organization\'s mentors and continue your educational journey.' :
             isOrganizationRenewal ? 'Select a plan to restore access for your organization\'s mentors.' :
             effectiveIsRenewal ? 'Choose a plan to continue your Artmosphere journey.' :
             effectiveIsNewAccount ? 'Support your child\'s imagination with guided art exploration, safe AI tools, and a calm space to create.' :
             isMentor ? 'Select the perfect mentor plan for your teaching needs and unlock powerful educational tools.' :
             'Support your child\'s imagination with guided art exploration, safe AI tools, and a calm space to create.'}
            </p>
          </header>

      <div className="max-w-6xl mx-auto px-5 py-6 md:px-10 md:py-8">

        {/* Billing Scenario Summary */}
        {showBillingSummary && billingScenario && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-blue-900 mb-4">Billing Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-blue-800 mb-2">{billingScenario.title}</h3>
                <p className="text-blue-700 text-sm">{billingScenario.description}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-blue-600 mb-1">Immediate Charge</div>
                <div className="text-2xl font-bold text-blue-900">
                  {billingScenario.immediateCharge > 0 ? `₹${billingScenario.immediateCharge}` : 'No charge'}
                </div>
                <div className="text-sm text-blue-600 mt-1">
                  Next cycle: ₹{billingScenario.nextBillingCycle}/month
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {billingScenario.benefits.map((benefit, index) => (
                <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  {benefit}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Add Mentor to Existing Plan Checkout */}
        {isAddMentorToExisting && (
          <div className="art-callout-strip p-6 mb-8">
            <h2 className="art-premium-heading text-2xl mb-4">Add Mentor to Your Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-[#6b5a48] mb-2">Current Plan Details</h3>
                <div className="space-y-2 text-[#8b6914]">
                  <p><strong>Plan:</strong> {addMentorData.planType?.replace('mentor-', '').toUpperCase()}</p>
                  <p><strong>Current Seats:</strong> {addMentorData.currentSeats}</p>
                  <p><strong>Adding:</strong> {addMentorData.mentorCount} mentor seat{addMentorData.mentorCount > 1 ? 's' : ''}</p>
                  <p><strong>New Total:</strong> {addMentorData.newTotalSeats} seats</p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white rounded-lg p-4 border border-amber-300">
                  <h4 className="font-semibold text-[#6b5a48] mb-3">Billing Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#8b6914]">Immediate Charge:</span>
                      <span className="font-bold text-amber-900">₹{addMentorData.proratedAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b6914]">Next Billing Cycle:</span>
                      <span className="font-bold text-amber-900">₹{addMentorData.nextBillingCycle}/month</span>
                    </div>
                    <div className="border-t border-amber-300 pt-2 mt-3">
                      <p className="text-sm text-[#8b6914]">
                        You'll be charged ₹{addMentorData.proratedAmount} immediately for the additional mentor seat{addMentorData.mentorCount > 1 ? 's' : ''}. 
                        Starting next billing cycle, you'll be charged ₹{addMentorData.nextBillingCycle}/month for all {addMentorData.newTotalSeats} seats.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => handleAddMentorCheckout()}
                disabled={busy}
                className="w-full sm:w-auto art-btn-primary px-4 sm:px-8 py-3 rounded-lg font-semibold text-sm sm:text-base md:text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {busy ? 'Processing...' : `Pay ₹${addMentorData.proratedAmount} & Add Mentor`}
              </button>
            </div>
          </div>
        )}
        
        {/* Organization Renewal Step-by-Step Flow */}
        {isOrganizationRenewal && (
          <div className="max-w-4xl mx-auto mb-8">
            {/* Progress Bar */}
            <div className="bg-white/90 backdrop-blur-sm border border-amber-200 rounded-2xl p-6 shadow-lg mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 1 ? 'art-btn-primary' : 'bg-gray-200 text-gray-500'
                }`}>1</div>
                <span className={`font-medium ${currentStep >= 1 ? 'text-[#5c4428]' : 'text-gray-500'}`}>Confirm Mentors</span>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-2">
                <div className={`h-full auth-premium-progress-bar transition-all duration-300 ${currentStep >= 2 ? 'w-full' : 'w-0'}`}></div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 2 ? 'art-btn-primary' : 'bg-gray-200 text-gray-500'
                }`}>2</div>
                <span className={`font-medium ${currentStep >= 2 ? 'text-[#5c4428]' : 'text-gray-500'}`}>Choose Plan</span>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-2">
                <div className={`h-full auth-premium-progress-bar transition-all duration-300 ${currentStep >= 3 ? 'w-full' : 'w-0'}`}></div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  currentStep >= 3 ? 'art-btn-primary' : 'bg-gray-200 text-gray-500'
                }`}>3</div>
                <span className={`font-medium ${currentStep >= 3 ? 'text-[#5c4428]' : 'text-gray-500'}`}>Checkout</span>
              </div>
            </div>
            </div>

            {/* Step 1: Confirm Mentors */}
            {currentStep === 1 && (
              <div className="bg-white/90 backdrop-blur-sm border border-amber-200 rounded-2xl p-6 shadow-lg">
                <h2 className="art-premium-heading text-2xl mb-4">Confirm Your Mentors</h2>
                <p className="art-premium-body-text mb-6">Your organization has {existingMentors.length} mentor{existingMentors.length !== 1 ? 's' : ''}. Please confirm which mentors should have access restored.</p>
                
                <div className="space-y-3 mb-6">
                  {existingMentors.map((mentor) => (
                    <div key={mentor.id} className="flex items-center justify-between p-3 border border-[#F5D99B]/55 rounded-lg bg-[#FFF9EF]">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedMentors.includes(mentor.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMentors([...selectedMentors, mentor.id]);
                            } else {
                              setSelectedMentors(selectedMentors.filter(id => id !== mentor.id));
                            }
                          }}
                          className="h-4 w-4 text-[#8b6914] focus:ring-amber-500 border-amber-300 rounded"
                        />
                        <div>
                          <div className="font-medium text-amber-900">{mentor.name || mentor.email || 'Unknown'}</div>
                          <div className="text-sm text-[#8b6914]">{mentor.email}</div>
                        </div>
                      </div>
                      <div className="text-sm text-[#8b6914]">
                        {mentor.status === 'active' ? 'Active' : 'Pending'}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Mentor */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowAddMentor(!showAddMentor)}
                    className="text-[#8b6914] hover:text-[#6b5a48] font-medium text-sm"
                  >
                    + Add New Mentor
                  </button>
                  
                  {showAddMentor && (
                    <div className="mt-4 p-4 border border-[#F5D99B]/55 rounded-lg bg-[#FFF9EF]">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-[#8b6914] mb-1">Mentor Name</label>
                          <input
                            type="text"
                            value={newMentorName}
                            onChange={(e) => setNewMentorName(e.target.value)}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="Enter mentor name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#8b6914] mb-1">Mentor Email</label>
                          <input
                            type="email"
                            value={newMentorEmail}
                            onChange={(e) => setNewMentorEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            placeholder="mentor@school.org"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={addNewMentor}
                          className="px-4 py-2 art-btn-primary rounded-lg transition-colors text-sm"
                        >
                          Add Mentor
                        </button>
                        <button
                          onClick={() => {
                            setShowAddMentor(false);
                            setNewMentorEmail('');
                            setNewMentorName('');
                          }}
                          className="art-btn-secondary px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      setMentorCount(selectedMentors.length);
                      setCurrentStep(2);
                    }}
                    disabled={selectedMentors.length === 0}
                    className="w-full sm:w-auto px-4 sm:px-6 py-3 art-btn-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base min-h-[44px]"
                  >
                    Next: Choose Plan ({selectedMentors.length} mentors)
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Choose Plan */}
            {currentStep === 2 && (
              <div className="bg-white/90 backdrop-blur-sm border border-amber-200 rounded-2xl p-6 shadow-lg">
                <h2 className="art-premium-heading text-2xl mb-4">Choose Your Plan</h2>
                <p className="art-premium-body-text mb-6">Select a plan for {mentorCount} mentor{mentorCount !== 1 ? 's' : ''}.</p>
                
                {/* Mentor Plans - Using same styling as main organization plans */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {generateMentorPlans(mentorCount, user?.mentorType === 'single').map((p) => (
                    <div
                      key={p.id}
                      onClick={() => setSelectedPlan(p.id)}
                      className={`cursor-pointer bg-white/90 rounded-2xl shadow border p-5 transition-all ${selectedPlan === p.id ? 'border-[#E8C97A] shadow-xl' : 'border-amber-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xl font-bold">{p.name}</div>
                        <div className="text-right">
                          {user?.mentorType === 'organization' ? (
                            <div>
                              <div className="text-[#8b6914] font-extrabold">Price for {mentorCount} mentor{mentorCount > 1 ? 's' : ''}</div>
                              <div className="text-2xl font-bold text-[#8b6914]">{p.price}</div>
                              <div className="text-sm text-gray-600">
                                ₹{(() => {
                                  if (p.id === 'mentor-basic') {
                                    return mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
                                  } else if (p.id === 'mentor-pro') {
                                    return mentorCount === 1 ? 1299 : mentorCount === 2 ? 1199 : mentorCount === 3 ? 1099 : mentorCount === 4 ? 1049 : 999;
                                  } else if (p.id === 'mentor-premium') {
                                    return mentorCount === 1 ? 1599 : mentorCount === 2 ? 1499 : mentorCount === 3 ? 1399 : mentorCount === 4 ? 1349 : 1299;
                                  }
                                  return 999;
                                })()}/mentor
                              </div>
                            </div>
                          ) : (
                            <div className="text-[#8b6914] font-extrabold">{p.price}</div>
                          )}
                        </div>
                      </div>
                      <ul className="mt-3 space-y-2 text-sm text-gray-800">
                        {/* Mentor features based on plan */}
                        <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span> Teaching Tools</li>
                        <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span> Student Management</li>
                        <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span> AI Feedback for Students</li>
                        <li className="flex items-start gap-2">{p.features.styleTransfer === '—' ? (<><span className="text-red-500 font-bold">✕</span> Style Transfer</>) : (<><span className="text-emerald-600 font-bold">✓</span> Style Transfer <span className="ml-1 text-gray-600">({p.features.styleTransfer})</span></>)}</li>
                        <li className="flex items-start gap-2">{p.features.storyboarding ? (<><span className="text-emerald-600 font-bold">✓</span> Storyboarding Studio</>) : (<><span className="text-red-500 font-bold">✕</span> Storyboarding Studio</>)}</li>
                        <li className="flex items-start gap-2">{p.features.coLearningSpace ? (<><span className="text-emerald-600 font-bold">✓</span> Co-Learning Space</>) : (<><span className="text-red-500 font-bold">✕</span> Co-Learning Space</>)}</li>
                        {p.features.prioritySupport && (
                          <li className="flex items-start gap-2"><span className="text-emerald-600 font-bold">✓</span> Priority Support</li>
                        )}
                      </ul>
                      {selectedPlan === p.id && (
                        <div className="mt-3 inline-block art-badge-selected">Selected</div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="art-btn-secondary w-full sm:w-auto px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-3 art-btn-primary rounded-lg transition-colors text-sm sm:text-base min-h-[44px]"
                  >
                    Continue to Checkout
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Checkout */}
            {currentStep === 3 && (
              <div className="art-premium-surface rounded-2xl p-6">
                <h2 className="art-premium-heading text-2xl mb-4">Complete Your Purchase</h2>
                <p className="art-premium-body-text mb-6">Review your order and complete the payment to restore access for your mentors.</p>
                
                {/* Order Summary */}
                <div className="art-callout-strip p-6 mb-6">
                  <h3 className="art-premium-heading mb-4 text-lg">Order Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#8b6914]">Plan:</span>
                      <span className="font-medium text-[#2f251b] capitalize">{selectedPlan}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b6914]">Mentors:</span>
                      <span className="font-medium text-[#2f251b]">{mentorCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8b6914]">Price per mentor:</span>
                      <span className="font-medium text-[#2f251b]">₹{pricing.perMentor}</span>
                    </div>
                    <div className="border-t border-[#F5D99B]/55 pt-3">
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-[#2f251b]">Total:</span>
                        <span className="text-[#2f251b]">₹{pricing.monthlySubtotal.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Button */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="art-btn-secondary w-full sm:w-auto px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Back to Plan Selection
                  </button>
                  <button
                    onClick={() => setConfirmOpen(true)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-3 art-btn-primary rounded-lg transition-colors text-sm sm:text-base min-h-[44px]"
                  >
                    Complete Purchase
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Regular Mentor Selection Flow */}
        {showMentorSelection && !isOrganizationRenewal && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white/90 backdrop-blur-sm border border-amber-200 rounded-2xl p-6 shadow-lg">
              <MentorSelection 
                ownerId={ownerId}
                onMentorsSelected={(mentors) => {
                  setSelectedMentors(mentors);
                  setMentorCount(mentors.length);
                  setShowMentorSelection(false);
                }}
              />
            </div>
          </div>
        )}

        {/* Renewal-specific message - only show for actual renewals, not new accounts */}
        {effectiveIsRenewal && !isMentorRenewal && !isOrganizationRenewal && !effectiveIsNewAccount && (
          <div className="max-w-5xl mx-auto mb-12">
            <div className="art-premium-surface relative overflow-hidden rounded-3xl p-8">
              <div className="pointer-events-none absolute top-0 right-0 h-64 w-64 rounded-full bg-[#F5E2C8]/30 blur-3xl" />
              <div className="pointer-events-none absolute bottom-0 left-0 h-48 w-48 rounded-full bg-[#FFF0DC]/35 blur-2xl" />
              
              <div className="relative z-10">
                <div className="flex items-start gap-6 mb-6">
                  <div className="auth-premium-callout-icon flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-2xl text-[#5c4428] shadow-lg">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="art-premium-heading text-2xl mb-2 tracking-tight">Welcome Back!</h2>
                    <p className="text-base text-[#6b5a48]/90 leading-relaxed">Your membership was cancelled and has expired. Choose a plan to renew and continue your Artmosphere journey.</p>
                  </div>
                </div>

                {/* Legacy renewal note kept but visually disabled */}
                {false && (
                  <div className="bg-white/80 backdrop-blur-sm border border-amber-200/80 rounded-xl p-5 shadow-inner">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-[#8b6914]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-sm text-[#6b5a48]/90 leading-relaxed">
                        <span className="font-semibold text-amber-900">Note:</span> Since you&apos;re renewing a cancelled membership, choose Artmosphere Studio or book codes to continue.
                        You can also use your Artmo Code to unlock <b>1 month</b> of Base access per code (redeeming more codes extends your end date).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mentor Renewal Message */}
        {isMentorRenewal && !showMentorSelection && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="art-premium-surface rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="auth-premium-callout-icon flex h-12 w-12 items-center justify-center rounded-full text-xl">👥</div>
                <div>
                  <h2 className="art-premium-heading text-xl">Organization Access Cancelled</h2>
                  <p className="text-[#6b5a48]">Your organization membership was cancelled and your mentors are unable to access their accounts. Choose a plan to restore access for {selectedMentors.length} mentor{selectedMentors.length !== 1 ? 's' : ''}.</p>
                </div>
              </div>
              <div className="art-callout-strip rounded-lg p-4">
                <p className="text-sm text-[#6b5a48]">
                  <strong>Selected Mentors:</strong> {selectedMentors.map(m => m.name).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Plan comparison (display only) */}
        {!isOrganizationRenewal && (
        <div className="max-w-6xl mx-auto mb-8">
       {isLoadingUserType ? (
       <div className="text-center py-8">
         <div className="text-lg text-[#8b6914]">Loading membership plans...</div>
       </div>
       ) : (
        <>
          {effectiveIsMentor ? (
            <div className={`grid gap-4 sm:gap-6 md:gap-8 px-2 sm:px-0 ${
              (effectiveIsRenewal && !effectiveIsMentor) 
                ? 'grid-cols-1 sm:grid-cols-2 max-w-4xl mx-auto' 
                : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
            }`}>
           {(() => {
            // Use admin plans if available, otherwise fallback to default plans
            let plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
            
            // Free trial removed; Artmo Code should remain selectable for both new and existing users.
             
             // Apply admin pricing/discounts and features if available
             if (!effectiveIsMentor && adminPlans) {
               plans = plans.map(plan => {
                 const adminPlan = adminPlans[plan.id];
                 if (adminPlan && adminPlan.active) {
                   let finalPrice = plan.price;
                  if (plan.id === 'artmocode') {
                     finalPrice = 'Free';
                   } else if (adminPlan.price) {
                     const basePrice = typeof adminPlan.price === 'number' ? adminPlan.price : parseFloat(adminPlan.price) || 199;
                     const discount = adminPlan.discount || 0;
                     const discountedPrice = basePrice * (1 - discount / 100);
                     finalPrice = `₹${Math.round(discountedPrice)}/mo`;
                   }
                   
                   // Apply admin feature settings if available
                   const adminFeatures = adminPlan.features || {};
                   const updatedFeatures = { ...plan.features };
                   Object.keys(updatedFeatures).forEach(featureKey => {
                     if (adminFeatures[featureKey]) {
                       updatedFeatures[featureKey] = {
                         ...updatedFeatures[featureKey],
                         ...adminFeatures[featureKey]
                       };
                     }
                   });
                   
                   return { ...plan, price: finalPrice, features: updatedFeatures };
                 }
                 return plan;
               });
             }
             
             console.log('🎯 Membership - Plans being displayed:', {
               isMentor: effectiveIsMentor,
               mentorType: effectiveMentorType,
               mentorCount: effectiveMentorCount,
               isSingleMentor,
               planCount: plans.length,
               plans: plans.map(p => ({ id: p.id, name: p.name, price: p.price })),
               adminPlans: adminPlans ? 'loaded' : 'not loaded',
               isRenewal: effectiveIsRenewal,
               filteredFreeTrial: false
             });
             return plans;
           })().map((p, index) => {
                const isSelected = selectedPlan === p.id;
                const isPremium = p.id === 'premium' || p.id === 'mentor-premium';
                return (
              <div
                key={p.id}
                onClick={async () => { 
                  // Refresh consent status before opening modal for under-13 users
                  if (p.id === 'artmocode' && isUnder13) {
                    console.log('🔄 Refreshing consent status before opening Artmo Code modal...');
                    await loadConsentStatus();
                    // Wait a bit for state to update
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                  
                  // For under-13 users clicking on Artmo Code, check consent first
                  if (p.id === 'artmocode' && isUnder13 && !consentConfirmed) {
                    setFormError('Please check your guardian email and provide consent to use Artmosphere features. Once consent is provided, you can redeem your Artmo Code for Base access (1 month per code).');
                    setConfirmOpen(false);
                    return;
                  }
                  
                  setSelectedPlan(p.id); 
                  setConfirmMode(p.id === 'artmocode' ? 'artmocode' : 'ask'); 
                  setConfirmOpen(true);
                  // Save to localStorage as fallback
                  try {
                    localStorage.setItem('selectedPlan', p.id);
                  } catch (e) {
                    console.warn('Could not save plan to localStorage:', e);
                  }
                }}
                className={`group relative cursor-pointer bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 transition-all duration-300 overflow-hidden transform hover:scale-[1.02] sm:hover:scale-105 hover:shadow-2xl ${
                  isSelected 
                    ? 'border-[#E8C97A] shadow-2xl shadow-amber-200/50 ring-2 sm:ring-4 ring-amber-200/50' 
                    : 'border-[#F5D99B]/45 hover:border-[#E8C97A]'
                } ${isPremium ? 'md:-mt-2 md:mb-2' : ''}`}
              >
                {/* Premium badge */}
                {isPremium && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="art-plan-popular-badge">
                      Popular
                    </div>
                  </div>
                )}
                
                {/* Gradient header */}
                <div className={`art-plan-card-header p-4 sm:p-6 transition-all duration-300 ${isSelected ? 'art-plan-card-header--selected' : ''}`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <div className={`text-xl sm:text-2xl font-extrabold ${isSelected ? 'text-white' : 'text-[#2f251b]'}`}>{p.name}</div>
                    </div>
                  <div className="text-right">
                    {isMentor && user?.mentorType === 'organization' ? (
                      <div>
                          <div className={`text-xs sm:text-sm font-semibold mb-1 ${isSelected ? 'text-white/90' : 'text-[#8b6914]'}`}>
                            Price for {mentorCount} mentor{mentorCount > 1 ? 's' : ''}
                          </div>
                          <div className={`text-2xl sm:text-3xl font-black ${isSelected ? 'text-white' : 'text-[#8b6914]'}`}>{p.price}</div>
                          <div className={`text-xs mt-1 ${isSelected ? 'text-white/80' : 'text-[#8b6914]'}`}>
                          ₹{(() => {
                            if (p.id === 'mentor-basic') {
                              return mentorCount === 1 ? 999 : mentorCount === 2 ? 899 : mentorCount === 3 ? 799 : mentorCount === 4 ? 749 : 699;
                            } else if (p.id === 'mentor-pro') {
                              return mentorCount === 1 ? 1299 : mentorCount === 2 ? 1199 : mentorCount === 3 ? 1099 : mentorCount === 4 ? 1049 : 999;
                            } else if (p.id === 'mentor-premium') {
                              return mentorCount === 1 ? 1599 : mentorCount === 2 ? 1499 : mentorCount === 3 ? 1399 : mentorCount === 4 ? 1349 : 1299;
                            }
                            return 999;
                          })()}/mentor
                        </div>
                      </div>
                    ) : (
                      <div>
                          <div className={`text-3xl font-black ${isSelected ? 'text-white' : 'text-[#8b6914]'}`}>{p.price}</div>
                        {isMentor && user?.mentorType === 'single' && (
                            <div className={`text-xs mt-1 ${isSelected ? 'text-white/80' : 'text-[#8b6914]'}`}>Single mentor pricing</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                </div>
                
                {/* Features list */}
                <div className="p-4 sm:p-6">
                  <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  {isMentor ? (
                    // Mentor features
                    <>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.teachingTools ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.teachingTools ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>Teaching Tools</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.studentManagement ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.studentManagement ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>Student Management</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.aiFeedback ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.aiFeedback ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>AI Feedback for Students</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.styleTransfer !== '—' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.styleTransfer !== '—' ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Style Transfer {p.features.styleTransfer !== '—' && <span className="text-gray-500">({p.features.styleTransfer})</span>}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.storyboarding ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.storyboarding ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>Storyboarding Studio</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features.coLearningSpace ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features.coLearningSpace ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>Co-Learning Space</span>
                        </li>
                      {p.features.prioritySupport && (
                          <li className="flex items-start gap-3">
                            <span className="text-lg font-bold text-emerald-600">✓</span>
                            <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>Priority Support</span>
                          </li>
                      )}
                    </>
                  ) : (
                    // Student features - new structure with limits
                    <>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.artHistoryAppreciation?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.artHistoryAppreciation?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Art History Appreciation
                            {p.features?.artHistoryAppreciation?.enabled && (
                              <span className="text-gray-500 text-xs ml-1">
                                ({p.features.artHistoryAppreciation.limit === 'unlimited' ? 'Unlimited' : `${p.features.artHistoryAppreciation.limit} allowed`})
                              </span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.artComparison?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.artComparison?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Art History Compare
                            {p.features?.artComparison?.enabled && (
                              <span className="text-gray-500 text-xs ml-1">
                                ({p.features.artComparison.limit === 'unlimited' ? 'Unlimited' : `${p.features.artComparison.limit} allowed`})
                              </span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.styleTransfer?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.styleTransfer?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Style Transfer
                            {p.features?.styleTransfer?.enabled && (
                              <span className="text-gray-500 text-xs ml-1">
                                ({p.features.styleTransfer.limit === 'unlimited' ? 'Unlimited' : `${p.features.styleTransfer.limit} allowed`})
                                {!p.features.styleTransfer.tutorial && ' - No tutorial'}
                              </span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.dailyActivities?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.dailyActivities?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Daily Activities
                            {p.features?.dailyActivities?.enabled && (
                              <span className="text-gray-500 text-xs ml-1">
                                ({p.features.dailyActivities.limit === 'unlimited' ? 'Unlimited' : `${p.features.dailyActivities.limit} allowed`})
                              </span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.individualArtAppreciation?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.individualArtAppreciation?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Individual Art Appreciation
                            {p.features?.individualArtAppreciation?.enabled && (
                              <span className="text-gray-500 text-xs ml-1">
                                ({p.features.individualArtAppreciation.limit === 'unlimited' ? 'Unlimited' : `${p.features.individualArtAppreciation.limit} allowed`})
                              </span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.coLearningSpace?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.coLearningSpace?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Co-Learning Space
                            {p.features?.coLearningSpace?.enabled && p.features.coLearningSpace.limit === 'unlimited' && (
                              <span className="text-gray-500 text-xs ml-1">(Unlimited)</span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.reimagineStudio?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.reimagineStudio?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Reimagine Studio
                            {p.features?.reimagineStudio?.enabled && p.features.reimagineStudio.limit === 'unlimited' && (
                              <span className="text-gray-500 text-xs ml-1">(Unlimited)</span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className={`text-lg font-bold ${p.features?.storyboarding?.enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.features?.storyboarding?.enabled ? '✓' : '✕'}
                          </span>
                          <span className={`flex-1 ${isSelected ? 'text-gray-700' : 'text-gray-800'}`}>
                            Storyboarding - "Once upon a click"
                            {p.features?.storyboarding?.enabled && p.features.storyboarding.limit === 'unlimited' && (
                              <span className="text-gray-500 text-xs ml-1">(Unlimited)</span>
                            )}
                          </span>
                        </li>
                    </>
                  )}
                </ul>
                  
                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="mt-6 pt-6 border-t border-amber-200">
                      <div className="art-badge-selected mx-auto flex w-fit items-center justify-center gap-2 px-4 py-2 text-sm">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Selected
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
          ) : (
            showTrialCta && (
              <div className="text-center py-4">
                <button
                  type="button"
                  onClick={async () => {
                    if (isUnder13) {
                      await loadConsentStatus();
                      await new Promise((resolve) => setTimeout(resolve, 100));
                      if (!consentConfirmed) {
                        setFormError(
                          'Please check your guardian email and provide consent to use Artmosphere features. Once consent is provided, you can redeem your Artmo Code for Base access (1 month per code).'
                        );
                        return;
                      }
                    }
                    setSelectedPlan('artmocode');
                    setConfirmMode('artmocode');
                    setConfirmOpen(true);
                  }}
                  className="inline-flex items-center px-6 py-3 rounded-full art-btn-primary font-semibold shadow-md text-sm md:text-base"
                >
                  {trialCtaText}
                </button>
              </div>
            )
          )}
        </>
       )}
        </div>
        )}
        {/* Removed legacy Student/Parent blocks */}
        {/* Legacy pricing cards removed per request; keeping trial & code flow below */}
        {false && isMentor && (
          <div className="flex items-center justify-center mb-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-amber-300/60 text-white shadow-sm">
              <span className="text-lg">⭐</span>
              <span className="text-sm font-semibold">Your personalized plan for {mentorCount} mentor{mentorCount>1?'s':''}</span>
            </div>
          </div>
        )}
        {false && isMentor && (
          <div className="flex justify-center mb-6">
            <button
              onClick={() => navigate('/mentor')}
              className="art-btn-primary rounded-xl px-6 py-3 font-semibold shadow-lg transition-all duration-200"
            >
              🎯 Go to Mentor Dashboard
            </button>
          </div>
        )}

        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${isOrganizationRenewal ? 'hidden' : 'hidden'}`}>
          {/* Monthly */}
          <div
            onClick={() => { setSelectedPlan('monthly'); setConfirmMode('ask'); setConfirmOpen(true); }}
            className={`cursor-pointer rounded-2xl border ${selectedPlan === 'monthly' ? 'border-[#E8C97A] shadow-xl' : 'border-[#F5D99B]/45 shadow'} bg-white overflow-hidden transition-all`}
          >
            <div className="art-plan-card-header art-plan-card-header--selected p-5 text-white">
              <div className="text-sm bg-white/20 inline-block px-3 py-1 rounded-full">billed monthly</div>
              <div className="mt-4 text-2xl font-extrabold">Monthly</div>
              <div className="mt-2 text-5xl font-black">
                {isMentor ? `₹${pricing.monthlySubtotal.toFixed(0)}` : '₹199'}
                <span className="text-lg font-semibold align-top">/mo</span>
              </div>
              {isMentor && (
                <div className="mt-1 text-sm text-white/90">₹{pricing.perMentor}/mentor • {mentorCount} mentor{mentorCount>1?'s':''}</div>
              )}
            </div>
            <div className="p-6">
              <ul className="space-y-2 text-[#6b5a48]">
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Access to AI Learning Space & private gallery</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Unlimited projects</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Free updates</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Community & basic support</li>
              </ul>
              {selectedPlan === 'monthly' && (
                <div className="mt-4 inline-block art-badge-selected">Selected</div>
              )}
            </div>
          </div>

          {/* 6 Months */}
          <div
            onClick={() => { setSelectedPlan('semiannual'); setConfirmMode('ask'); setConfirmOpen(true); }}
            className={`cursor-pointer rounded-2xl border ${selectedPlan === 'semiannual' ? 'border-[#E8C97A] shadow-xl' : 'border-[#F5D99B]/45 shadow'} bg-white overflow-hidden transition-all`}
          >
            <div className="art-plan-card-header art-plan-card-header--selected p-5 text-white">
              <div className="text-sm bg-white/20 inline-block px-3 py-1 rounded-full">billed every 6 months</div>
              <div className="mt-4 text-2xl font-extrabold">6 Months</div>
              <div className="mt-2 text-5xl font-black">{isMentor ? `₹${Math.round(pricing.monthlySubtotal * 6 * (1 - semiannualDiscountPct/100))}` : '$49'}<span className="text-lg font-semibold align-top">/6mo</span></div>
              {isMentor && (
                <div className="mt-1 text-sm text-white/90">₹{Math.round(pricing.perMentor * (1 - semiannualDiscountPct/100))}/mentor (effective) • {mentorCount} mentor{mentorCount>1?'s':''} • totals include {semiannualDiscountPct}% discount</div>
              )}
            </div>
            <div className="p-6">
              <ul className="space-y-2 text-[#6b5a48]">
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Everything in Monthly</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Save vs monthly billing</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Priority queue for AI tasks</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Early access features</li>
              </ul>
              {selectedPlan === 'semiannual' && (
                <div className="mt-4 inline-block art-badge-selected">Selected</div>
              )}
            </div>
          </div>

          {/* Yearly */}
          <div
            onClick={() => { setSelectedPlan('annual'); setConfirmMode('ask'); setConfirmOpen(true); }}
            className={`cursor-pointer rounded-2xl border ${selectedPlan === 'annual' ? 'border-[#E8C97A] shadow-xl' : 'border-[#F5D99B]/45 shadow'} bg-white overflow-hidden transition-all`}
          >
            <div className="art-plan-card-header art-plan-card-header--selected p-5 text-white">
              <div className="text-sm bg-white/20 inline-block px-3 py-1 rounded-full">billed annually</div>
              <div className="mt-4 text-2xl font-extrabold">Yearly</div>
              <div className="mt-2 text-5xl font-black">{isMentor ? `₹${Math.round(pricing.monthlySubtotal * 12 * (1 - annualDiscountPct/100))}` : '$89'}<span className="text-lg font-semibold align-top">/yr</span></div>
              {isMentor && (
                <div className="mt-1 text-sm text-white/90">₹{Math.round(pricing.perMentor * (1 - annualDiscountPct/100))}/mentor (effective) • {mentorCount} mentor{mentorCount>1?'s':''} • totals include {annualDiscountPct}% discount</div>
              )}
            </div>
            <div className="p-6">
              <ul className="space-y-2 text-[#6b5a48]">
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Everything in 6 Months</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Best value overall</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> VIP support</li>
                <li className="flex items-start"><span className="text-emerald-500 mr-2">✓</span> Member showcases & events</li>
              </ul>
              {selectedPlan === 'annual' && (
                <div className="mt-4 inline-block art-badge-selected">Selected</div>
              )}
            </div>
          </div>
        </div>

        {/* Removed old Artmo book box here – moved below pricing section */}

        {/* New Student/Parent pricing section */}
        {!isMentor && (
          <div className="max-w-5xl mx-auto my-10">
            <div className="max-w-4xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[#f5d99b]/70 bg-gradient-to-r from-[#fffdf9] to-[#fff9ef] px-4 py-3.5 md:px-5 shadow-sm">
              <p className="text-sm leading-relaxed text-[#5a4632]">
                <span className="font-semibold text-[#2f251b]">Already have an Artmo code?</span> Redeem it here to unlock Studio access.
              </p>
              <button
                type="button"
                onClick={openArtmoRedeemModal}
                className="art-btn-primary shrink-0 inline-flex min-h-[44px] items-center justify-center rounded-full px-5 text-sm font-semibold"
              >
                Redeem Artmo code
              </button>
            </div>
            {/* Plan cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7 max-w-4xl mx-auto">
              {['artmocode', 'basic'].map((planId) => {
                const monthlyStudioCfg = getPlanConfig({ planTier: 'base', billingInterval: 'monthly' });
                const isArtmoCode = planId === 'artmocode';
                const isSelected = selectedPlan === planId;

                return (
                  <button
                    key={planId}
                    type="button"
                    onClick={() => {
                      if (planId === 'artmocode') {
                        navigate('/create-books');
                      } else {
                        setIncludeUnlimitedStudioAddon(false);
                        setSelectedPlan(planId);
                        setShowIntervalPicker(true);
                      }
                    }}
                    className={`art-membership-plan-card w-full ${
                      isSelected ? 'art-membership-plan-card--selected' : ''
                    }`}
                  >
                    <div className="mb-3">
                      <div className="art-membership-plan-label mb-1">
                        {isArtmoCode ? 'Books → Studio' : 'Subscription'}
                      </div>
                      <div className="art-membership-plan-title">
                        {isArtmoCode
                          ? 'Buy Artmosphere books and get free access to Artmosphere Studio'
                          : 'Artmosphere Studio subscription'}
                      </div>
                    </div>

                    {isArtmoCode ? (
                      <div className="mb-4">
                        <div className="art-membership-plan-price">1 month / code</div>
                        <div className="art-membership-plan-muted">
                          Browse the collection to buy a book; each includes an Artmo code for Studio access.
                        </div>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <div className="art-membership-plan-price flex items-baseline gap-1 flex-wrap">
                          <span>₹{monthlyStudioCfg ? monthlyStudioCfg.amount.toLocaleString('en-IN') : '—'}</span>
                          <span className="text-sm font-semibold text-[#8a7357]">/month from</span>
                        </div>
                        <div className="art-membership-plan-muted">
                          Optional unlimited upgrade +₹{publicMembershipAddonRate}/month at checkout
                        </div>
                      </div>
                    )}

                    <div className="border-t border-[#f5d99b]/50 pt-3 mt-3">
                      <div className="art-membership-plan-label mb-2">What&apos;s included</div>
                      <ul className="art-membership-feature-list space-y-1.5">
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 mt-0.5 font-semibold">✓</span>
                          <span>Guided activities and daily creative prompts</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 mt-0.5 font-semibold">✓</span>
                          <span>Safe AI tools for exploring art styles</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-600 mt-0.5 font-semibold">✓</span>
                          <span>Personal gallery &amp; co-learning space</span>
                        </li>
                        {!isArtmoCode && (
                          <li className="flex items-start gap-2 text-[#6b5a48]">
                            <span className="text-[#b45309] mt-0.5 font-semibold">+</span>
                            <span>Add unlimited Studio access at checkout (higher limits, storyboarding, full toolkit)</span>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="mt-5">
                      <span
                        className={`art-btn-primary inline-flex min-h-[48px] w-full items-center justify-center rounded-full px-4 text-sm font-semibold ${
                          isSelected ? 'ring-2 ring-[#2f251b]/15 ring-offset-2 ring-offset-[#fffdf9]' : ''
                        }`}
                      >
                        {isArtmoCode
                          ? 'Browse Artmosphere books'
                          : isSelected
                            ? 'Selected · Continue'
                            : 'Choose this option'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-xl border border-rose-200/80 bg-[#fff9ef] px-4 py-3 text-center text-sm text-rose-800">
            {error}
          </p>
        )}
      </div>
        </div>

      {/* Billing interval picker (Studio subscription) */}
      {showIntervalPicker && (
        <div className="art-membership-modal-backdrop fixed inset-0 z-[200100] flex items-center justify-center p-4 sm:p-6">
          <div className="art-membership-modal-panel w-full max-w-2xl p-6 sm:p-8">
            <div className="art-premium-surface__gold-bar mb-5 rounded-t-[24px]" aria-hidden />
            <p className="art-premium-eyebrow text-center mb-1">Artmosphere Studio</p>
            <h3 className="font-serif text-2xl font-semibold text-[#2f251b] mb-2 text-center">
              Choose how you&apos;d like to be billed
            </h3>
            <p className="text-xs text-[#8a7357] mb-5 text-center">
              You can change or cancel your plan anytime.
            </p>
            <div className="flex flex-col gap-3 md:flex-row md:gap-5">
              {[
                { id: 'monthly', label: '1 month', note: 'Billed every month · full flexibility' },
                { id: 'quarterly', label: '3 months', note: 'Save 5% vs paying monthly' },
                { id: 'semiannual', label: '6 months', note: 'Save 10% vs monthly' },
              ].map((opt) => {
                const cfg = getPlanConfig({
                  planTier: 'base',
                  billingInterval: opt.id,
                });
                const isActive = billingInterval === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setBillingInterval(opt.id);
                      setIncludeUnlimitedStudioAddon(false);
                      setShowIntervalPicker(false);
                      setConfirmMode('paid');
                      setConfirmOpen(true);
                    }}
                    className={`art-choice-btn flex flex-1 flex-col gap-1 text-left ${
                      isActive ? 'art-choice-btn--active' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-base font-semibold text-[#2f251b]">{opt.label}</div>
                        <div className="text-[11px] font-medium text-[#8a7357]">{opt.note}</div>
                      </div>
                      {isActive && (
                        <span className="art-badge-selected !mt-0 text-[10px]">
                          Selected
                        </span>
                      )}
                    </div>
                    {cfg && (
                      <div className="mt-2 text-base font-semibold text-[#2f251b]">
                        ₹{cfg.amount.toLocaleString('en-IN')}
                        <span className="text-xs font-normal text-[#8b6914] ml-1">
                          /
                          {opt.id === 'yearly'
                            ? 'year'
                            : opt.id === 'semiannual'
                            ? '6 months'
                            : opt.id === 'quarterly'
                            ? '3 months'
                            : 'month'}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setShowIntervalPicker(false)}
              className="art-btn-secondary mt-5 w-full min-h-[44px] rounded-full text-sm font-semibold"
            >
              Back
            </button>
          </div>
        </div>
      )}
      {/* Confirm Selection Modal */}
      {confirmOpen && (
        <div
          className="art-membership-modal-backdrop fixed inset-0 z-[200110] flex items-center justify-center p-3 sm:p-4"
        >
          <div
            id="confirm-plan-modal"
            className={`art-membership-modal-panel relative w-full overflow-y-auto ${
              confirmMode === 'artmocode'
                ? 'max-w-5xl xl:max-w-6xl max-h-[min(92dvh,880px)] p-0'
                : `max-h-[90vh] p-6 sm:p-8 ${
                    confirmMode === 'paid'
                      ? 'w-[calc(100vw-4cm)] max-w-[calc(100vw-4cm)]'
                      : 'max-w-md'
                  }`
            }`}
          >
            <div className="art-premium-surface__gold-bar" aria-hidden />
            {confirmMode !== 'artmocode' && (
              <div className={confirmMode === 'paid' ? 'mb-6 sm:mb-8' : 'mb-2'}>
                {confirmMode === 'paid' && (
                  <button
                    type="button"
                    onClick={() => setConfirmOpen(false)}
                    className="mb-4 inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-amber-950 bg-white border border-amber-200/90 shadow-sm hover:bg-amber-50/80 hover:border-amber-300 transition-colors"
                  >
                    <span aria-hidden className="text-base leading-none">
                      ←
                    </span>
                    Back to membership
                  </button>
                )}
                <p className="art-premium-eyebrow mb-1">Artmosphere Studio</p>
                <h3
                  className={
                    confirmMode === 'paid'
                      ? 'font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-[#2f251b]'
                      : 'font-serif text-xl font-semibold text-[#2f251b]'
                  }
                >
                  {confirmMode === 'ask' ? 'Confirm plan selection' : 'Checkout'}
                </h3>
                {confirmMode === 'paid' && (
                  <p className="mt-1.5 text-sm font-medium text-[#5a4632]">
                    Review your plan — amounts shown as a monthly guide and as this period&apos;s invoice.
                  </p>
                )}
              </div>
            )}
            
            {/* Show consent message for under-13 users on Artmo Code */}
            {confirmMode === 'ask' && isUnder13 && !consentConfirmed && selectedPlan === 'artmocode' && (
              <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5 text-[#8b6914]" aria-hidden>
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-amber-900 font-semibold mb-1">Consent Required</p>
                    <p className="text-[#6b5a48] text-sm">
                      Please check your guardian email and provide consent to use Artmosphere features. Once consent is provided, you can redeem your Artmo Code for Base access (1 month per code).
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {confirmMode === 'ask' && (() => {
              // Get the actual plan name from the plans array
              const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
              const selectedPlanData = plans.find(p => p.id === selectedPlan);
              const planDisplayName = selectedPlanData?.name || 
                (selectedPlan === 'annual' ? 'Yearly' :
                selectedPlan === 'semiannual' ? '6 Months' :
                selectedPlan === 'basic' ? 'Basic' :
                selectedPlan === 'artmocode' ? 'Artmo Code' :
                selectedPlan === 'pro' ? 'Pro' :
                selectedPlan === 'premium' ? 'Premium' :
                selectedPlan === 'mentor-basic' ? 'Mentor Basic' :
                selectedPlan === 'mentor-pro' ? 'Mentor Pro' :
                selectedPlan === 'mentor-premium' ? 'Mentor Premium' : 'Monthly');
              
              return (
                <p className="art-premium-body-text mb-6">Are you sure you want to select the <b>{planDisplayName}</b> plan?</p>
              );
            })()}
            {confirmMode === 'artmocode' && (
              <div className="space-y-0">
                <div className="relative bg-gradient-to-br from-amber-400 via-orange-400 to-fuchsia-400 px-5 sm:px-8 lg:px-10 pt-4 pb-3 text-center text-white">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight">
                    Your book code, your Studio time
                  </h2>
                  <p className="mt-2 text-xs sm:text-sm text-white/95 leading-snug max-w-3xl mx-auto">
                    Type the special code from your Artmosphere book. Each code adds{' '}
                    <strong>one whole month</strong> of creative play — no payment today.
                  </p>
                </div>
                <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-3 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 lg:items-start">
                    <div>
                      <label
                        className="block text-xs sm:text-sm font-bold text-amber-950 mb-0.5"
                        htmlFor="artmo-code-row-0"
                      >
                        Step 1 — Type your code(s)
                      </label>
                      <p className="text-[11px] sm:text-xs text-[#6b5a48]/90 mb-2">
                        More than one book? Add each code — we&apos;ll stack the months for you.
                      </p>
                      <div className="space-y-2">
                        {artmoCodeRows.map((row, i) => (
                          <div key={`artmo-row-${i}`} className="flex gap-2 items-stretch">
                            <input
                              id={i === 0 ? 'artmo-code-row-0' : undefined}
                              type="text"
                              value={row}
                              onChange={(e) => {
                                const v = e.target.value;
                                setArtmoCodeRows((prev) => {
                                  const next = [...prev];
                                  next[i] = v;
                                  return next;
                                });
                              }}
                              placeholder="Code from inside your book"
                              className="flex-1 min-w-0 rounded-xl border-2 border-amber-200/90 bg-white px-3 py-2 text-sm text-amber-950 placeholder:text-amber-400/90 shadow-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                              autoComplete="off"
                              autoCorrect="off"
                              spellCheck={false}
                            />
                            {artmoCodeRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setArtmoCodeRows((prev) => prev.filter((_, j) => j !== i))
                                }
                                className="shrink-0 rounded-lg px-2.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setArtmoCodeRows((prev) => [...prev, ''])}
                        className="mt-2 inline-flex items-center gap-1 rounded-full border-2 border-dashed border-amber-300 bg-amber-50/70 px-3 py-1.5 text-xs sm:text-sm font-semibold text-amber-900 hover:bg-amber-100/90 transition-colors"
                      >
                        <span className="text-base leading-none">+</span> Add another code
                      </button>
                    </div>

                    <div className="rounded-xl border border-amber-100 bg-gradient-to-b from-amber-50/95 to-white p-3 sm:p-4 shadow-sm">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-amber-900/85">
                        What you unlock
                      </h3>
                      <ul className="mt-2 space-y-1.5 text-xs sm:text-sm text-amber-950 leading-snug">
                        <li className="flex gap-2 items-start">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                          Your own Artmosphere Studio to explore
                        </li>
                        <li className="flex gap-2 items-start">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                          Fun daily prompts &amp; gentle art activities
                        </li>
                        <li className="flex gap-2 items-start">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                          Safe AI helpers made for young artists
                        </li>
                        <li className="flex gap-2 items-start">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                          A cozy gallery &amp; spaces to create with friends
                        </li>
                      </ul>
                      <p className="mt-2 rounded-lg bg-white/90 border border-amber-100 px-2 py-1.5 text-[11px] sm:text-xs text-amber-900/85 leading-snug">
                        <span className="font-semibold">Want even more later?</span> Grown-ups can add a{' '}
                        <strong>Studio boost</strong> for bigger adventures (more tools &amp; limits). You don&apos;t need it to start.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-xl bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-200/70 px-4 py-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800/75">
                          Today
                        </p>
                        <p className="text-lg font-bold text-emerald-900">Free with your book</p>
                      </div>
                      <div className="text-left sm:text-right">
                        <p className="text-[11px] font-semibold text-emerald-800/75">Total due</p>
                        <p className="text-3xl font-black text-emerald-600">₹0</p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-sky-200/70 bg-sky-50/60 px-3 py-2.5 text-center text-xs sm:text-sm text-sky-950/90 leading-snug flex items-center justify-center">
                      We start your time from today — or add on if you already have days left.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {confirmMode === 'paid' && (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* Left: Plan summary */}
                <div className="rounded-2xl border border-amber-200/55 bg-gradient-to-br from-[#fffdfb] via-amber-50/35 to-orange-50/25 p-5 sm:p-6 shadow-[0_12px_48px_-16px_rgba(146,64,14,0.18)] ring-1 ring-amber-900/5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6b5a48]/70 mb-2">
                    Your selection
                  </p>
                  <div className="text-xl sm:text-2xl font-semibold tracking-tight text-stone-900">
                    {selectedPlan === 'basic' ? 'Artmosphere Studio' : 'Plan'}
                  </div>
                  {!effectiveIsMentor && selectedPlan !== 'artmocode' && (() => {
                    const cfg = getPlanConfig({
                      planTier: 'base',
                      billingInterval,
                    });
                    if (!cfg) return null;
                    const months = monthsInBillingPeriod(billingInterval);
                    const addonPart = includeUnlimitedStudioAddon ? publicMembershipAddonRate * months : 0;
                    const total = cfg.amount + addonPart;
                    const periodLabel = periodLabelForBilling(billingInterval);
                    return (
                      <>
                        <div className="mt-5 rounded-2xl border border-amber-200/40 bg-white/70 backdrop-blur-sm px-4 py-4 sm:px-5 sm:py-5">
                          <p className="text-xs font-medium text-amber-900/80 mb-1">Artmosphere Studio</p>
                          <div className="text-sm text-stone-600 font-medium mb-2">
                            {(() => {
                              const hasUnlimitedAccess =
                                selectedPlan === 'pro' ||
                                selectedPlan === 'premium' ||
                                (selectedPlan === 'basic' && includeUnlimitedStudioAddon);
                              return hasUnlimitedAccess
                                ? 'Unlimited access included · Storyboarding unlocked'
                                : 'Limited access included';
                            })()}
                          </div>

                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                            <span className="text-3xl sm:text-4xl font-semibold tabular-nums tracking-tight text-stone-900">
                              ₹{total.toLocaleString('en-IN')}
                            </span>
                            <span className="text-base sm:text-lg font-medium text-stone-600">for {periodLabel}</span>
                          </div>

                          {selectedPlan === 'basic' && (
                            <p className="mt-2 text-xs leading-relaxed text-stone-600">
                              {includeUnlimitedStudioAddon ? (
                                <>
                                  Unlimited add-on: <span className="font-semibold text-emerald-700">Storyboarding — Once Upon a Click</span>
                                  {' · '}
                                  ₹{publicMembershipAddonRate}/month{' '}
                                  <span className="text-stone-500 font-medium">({addonPart.toLocaleString('en-IN')} this period)</span>
                                </>
                              ) : (
                                <>
                                  Add the Unlimited add-on to unlock <span className="font-semibold text-stone-800">Storyboarding</span>.
                                </>
                              )}
                            </p>
                          )}
                        </div>

                        {selectedPlan === 'basic' && (
                          <label className="group/unl mt-5 flex cursor-pointer items-start gap-3.5 rounded-2xl border border-violet-200/70 bg-gradient-to-br from-[#faf7ff] via-white to-amber-50/40 p-4 sm:p-5 text-left shadow-[0_10px_40px_-12px_rgba(91,33,182,0.12)] ring-1 ring-violet-900/[0.04] transition hover:border-violet-300 hover:shadow-[0_14px_44px_-10px_rgba(91,33,182,0.16)]">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 shrink-0 rounded border-violet-400 text-violet-600 focus:ring-2 focus:ring-violet-400 focus:ring-offset-1"
                              checked={includeUnlimitedStudioAddon}
                              onChange={(e) => setIncludeUnlimitedStudioAddon(e.target.checked)}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block font-serif text-base font-semibold tracking-tight text-[#2c2140]">
                                Add unlimited access
                              </span>
                              <span className="mt-0.5 block text-[11px] font-medium uppercase tracking-[0.14em] text-violet-700/75">
                                Studio boost · one add-on
                              </span>
                              <ul className="mt-3 space-y-2 text-[13px] leading-snug text-stone-700 sm:text-sm">
                                <li className="flex gap-2.5">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-sm" aria-hidden />
                                  <span>Unlimited access to all Artmosphere features</span>
                                </li>
                                <li className="flex gap-2.5">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-sm" aria-hidden />
                                  <span>Unlimited Art History learnings</span>
                                </li>
                                <li className="flex gap-2.5">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-sm" aria-hidden />
                                  <span>Unlimited learning about 50+ art style tutorials</span>
                                </li>
                                <li className="flex gap-2.5">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 shadow-sm" aria-hidden />
                                  <span className="font-medium text-stone-800">Storyboarding unlocked</span>
                                </li>
                              </ul>
                              <p className="mt-3 rounded-lg border border-violet-100/90 bg-white/70 px-3 py-2 text-[11px] leading-relaxed text-stone-600">
                                Includes <span className="font-semibold text-violet-900">Once Upon a Click</span> — create characters, write stories, and print storybooks.
                              </p>
                              {includeUnlimitedStudioAddon && (
                                <p className="mt-2 text-xs font-semibold text-violet-900">
                                  +₹{publicMembershipAddonRate}/month · ₹{addonPart.toLocaleString('en-IN')} this period
                                </p>
                              )}
                            </span>
                          </label>
                        )}
                      </>
                    );
                  })()}
                  {effectiveIsMentor && (
                    <div className="mt-2 text-sm text-[#6b5a48]">
                      Mentors: {mentorCount} • ₹{effectivePerMentor}/mentor/mo
                    </div>
                  )}
                  <div className="mt-5">
                    {(() => {
                      const months = monthsInBillingPeriod(billingInterval);
                      const addonPart = includeUnlimitedStudioAddon ? publicMembershipAddonRate * months : 0;
                      const storyboardingUnlocked =
                        selectedPlan === 'basic' ? includeUnlimitedStudioAddon : selectedPlan === 'pro' || selectedPlan === 'premium';
                      const planTierForCalc =
                        selectedPlan === 'basic' || selectedPlan === 'base'
                          ? 'base'
                          : selectedPlan === 'pro' || selectedPlan === 'premium'
                            ? 'pro'
                            : 'base';
                      const cfgForCalc = getPlanConfig({ planTier: planTierForCalc, billingInterval });
                      const baseAmount = cfgForCalc?.amount || 0;
                      const monthlyEstimate = months > 0 ? Math.round((baseAmount + addonPart) / months) : baseAmount + addonPart;

                      return (
                        <>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2">
                            WHAT YOU GET @ just <span className="font-bold text-stone-700">₹{monthlyEstimate}</span> / month
                          </p>
                          <ul className="space-y-1.5 text-stone-800 text-sm">
                            <li className="flex items-start gap-2">
                              <span className="text-emerald-600 font-bold">✓</span>
                              <span>
                                Artmosphere Studio {includeUnlimitedStudioAddon ? 'unlimited' : 'limited'} access
                              </span>
                            </li>
                          {storyboardingUnlocked ? (
                            <li className="flex items-start gap-2">
                              <span className="text-violet-600 font-bold">★</span>
                              <span>
                                Unlimited add-on with Storyboarding — Once Upon a Click
                                {selectedPlan === 'basic' ? (
                                  <>
                                    {' '}
                                    · +₹{publicMembershipAddonRate}/month ({addonPart.toLocaleString('en-IN')} this period)
                                  </>
                                ) : null}
                              </span>
                            </li>
                          ) : (
                            <li className="flex items-start gap-2">
                              <span className="text-stone-500 font-bold">✕</span>
                              <span>Storyboarding unlocks with the Unlimited add-on</span>
                            </li>
                          )}
                          </ul>
                        </>
                      );
                    })()}
                  </div>
                  <div className="mt-5 rounded-2xl border border-amber-200/70 bg-white/70 p-4 sm:p-5 text-sm">
                {(() => {
                  // Get updated plans with admin pricing/features (same logic as membership page)
                  let plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
                  if (!effectiveIsMentor && adminPlans) {
                    plans = plans.map(plan => {
                      const adminPlan = adminPlans[plan.id];
                      if (adminPlan && adminPlan.active) {
                        let finalPrice = plan.price;
                        if (plan.id === 'artmocode') {
                          finalPrice = 'Free';
                        } else if (adminPlan.price) {
                          const basePrice = typeof adminPlan.price === 'number' ? adminPlan.price : parseFloat(adminPlan.price) || 199;
                          const discount = adminPlan.discount || 0;
                          const discountedPrice = basePrice * (1 - discount / 100);
                          finalPrice = `₹${Math.round(discountedPrice)}/mo`;
                        }
                        const adminFeatures = adminPlan.features || {};
                        const updatedFeatures = { ...plan.features };
                        Object.keys(updatedFeatures).forEach(featureKey => {
                          if (adminFeatures[featureKey]) {
                            updatedFeatures[featureKey] = {
                              ...updatedFeatures[featureKey],
                              ...adminFeatures[featureKey]
                            };
                          }
                        });
                        return { ...plan, price: finalPrice, features: updatedFeatures };
                      }
                      return plan;
                    });
                  }
                  const selectedPlanData = plans.find(p => p.id === selectedPlan);

                  /** Must match the headline totals above (period price + full-period add-on), not the /mo label. */
                  let amount = 0;
                  if (!effectiveIsMentor && selectedPlan === 'basic') {
                    const cfg = getPlanConfig({ planTier: 'base', billingInterval });
                    if (cfg) {
                      const months = monthsInBillingPeriod(billingInterval);
                      const addonPart = includeUnlimitedStudioAddon ? publicMembershipAddonRate * months : 0;
                      amount = cfg.amount + addonPart;
                    }
                  } else if (!effectiveIsMentor && (selectedPlan === 'pro' || selectedPlan === 'premium')) {
                    const cfg = getPlanConfig({ planTier: 'pro', billingInterval });
                    if (cfg) amount = cfg.amount;
                  } else if (selectedPlanData && selectedPlanData.price) {
                    const priceStr = selectedPlanData.price;
                    if (priceStr === 'Free') {
                      amount = 0;
                    } else {
                      const priceMatch = priceStr.match(/₹(\d+)/);
                      if (priceMatch) {
                        amount = parseInt(priceMatch[1], 10);
                      }
                    }
                  }
                  if (!amount && !effectiveIsMentor && selectedPlan === 'basic') {
                    const cfg = getPlanConfig({ planTier: 'base', billingInterval: 'monthly' });
                    if (cfg) {
                      const addonPart = includeUnlimitedStudioAddon ? publicMembershipAddonRate : 0;
                      amount = cfg.amount + addonPart;
                    }
                  }
                  if (!amount) {
                    amount =
                      selectedPlan === 'basic' || selectedPlan === 'base' ? 199 :
                      selectedPlan === 'premium' ? 749 :
                      pricing.base;
                  }

                  const gst = amount * 0.18;
                  const total = amount + gst;

                  return (
                    <>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">
                        Payment summary (tax)
                      </p>
                      <div className="space-y-2 text-stone-700">
                        <div className="flex justify-between gap-2">
                          <span>Subtotal (this invoice)</span>
                          <span className="text-right tabular-nums">
                            ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span>GST (18%)</span>
                          <span className="text-right tabular-nums">
                            ₹{gst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2 pt-2 mt-2 border-t border-stone-200/80 font-semibold text-stone-900 text-base">
                          <span>Total due today</span>
                          <span className="text-right tabular-nums">
                            ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-stone-500 leading-relaxed">
                        Matches the period total above, plus GST. The monthly figure is an average to help you compare —
                        your card is charged once for the full period.
                      </p>
                    </>
                  );
                })()}
                  </div>
                </div>

                {/* Right: Payment + Billing */}
                <div className="rounded-2xl border border-amber-200/70 bg-white/70 backdrop-blur-sm p-5 sm:p-6 shadow-[0_8px_32px_-12px_rgba(0,0,0,0.08)]">
                  {(() => {
                    // Check if this is an Artmo Code plan (no payment required)
                    const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
                    const selectedPlanData = plans.find(p => p.id === selectedPlan);
                    
                    if (selectedPlanData && selectedPlanData.requiresPayment === false) {
                      // Artmo Code redemption doesn't require payment details (even if the selected plan is paid).
                      return (
                        <div className="p-6 rounded-xl border-2 border-emerald-300 bg-emerald-50">
                          <div className="text-center">
                            <div className="text-2xl mb-2">🎉</div>
                            <div className="text-lg font-bold text-emerald-900 mb-2">Artmo Code - No Payment Required</div>
                            <div className="text-sm text-emerald-800">
                              Click &quot;Redeem Artmo Code&quot; to apply your code(s). Each valid code adds 1 month of Base access — no card details needed.
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Paid plans - show payment form
                    const field =
                      'w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow';
                    return (
                      <>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">
                          Payment method
                        </p>
                        <div className="flex gap-2 mb-4 p-1 rounded-xl bg-white/70 border border-amber-200/70">
                          <button
                            type="button"
                            onClick={() => setPayMethod('card')}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                              payMethod === 'card'
                                ? 'bg-white text-stone-900 shadow-sm ring-1 ring-amber-200/90'
                                : 'text-stone-600 hover:text-stone-900'
                            }`}
                          >
                            Card
                          </button>
                          <button
                            type="button"
                            onClick={() => setPayMethod('upi')}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                              payMethod === 'upi'
                                ? 'bg-white text-stone-900 shadow-sm ring-1 ring-amber-200/90'
                                : 'text-stone-600 hover:text-stone-900'
                            }`}
                          >
                            UPI
                          </button>
                        </div>
                        {payMethod === 'card' ? (
                          <div className="space-y-3">
                            <input
                              value={cardEmail}
                              onChange={(e) => setCardEmail(e.target.value)}
                              className={field}
                              placeholder="Email (required)"
                              aria-invalid={!cardEmail ? 'true' : 'false'}
                            />
                            <input
                              value={cardName}
                              onChange={(e) => setCardName(e.target.value)}
                              className={field}
                              placeholder="Name on card (required)"
                              aria-invalid={!cardName ? 'true' : 'false'}
                            />
                            <input
                              value={cardNumber}
                              onChange={(e) => setCardNumber(e.target.value)}
                              className={field}
                              placeholder="Card number (required)"
                              aria-invalid={cardNumber.replace(/\D/g, '').length < 12 ? 'true' : 'false'}
                            />
                            <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-2">
                              <input
                                value={cardExp}
                                onChange={(e) => setCardExp(formatExpiryInput(e.target.value))}
                                className={field}
                                inputMode="numeric"
                                autoComplete="cc-exp"
                                maxLength={5}
                                placeholder="MM/YY"
                                aria-invalid={!/^((0[1-9])|(1[0-2]))\/\d{2}$/.test(cardExp || '') ? 'true' : 'false'}
                              />
                              <input
                                value={cardCvc}
                                onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                className={`${field} text-center`}
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                maxLength={4}
                                placeholder="CVC"
                                aria-invalid={(cardCvc || '').trim().length < 3 ? 'true' : 'false'}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <input
                              value={upiId}
                              onChange={(e) => setUpiId(e.target.value)}
                              className={field}
                              placeholder="your-upi-id@bank (required)"
                              aria-invalid={!isValidUpi() ? 'true' : 'false'}
                            />
                            <p className="text-xs text-stone-500">UPI is a placeholder until the live gateway is connected.</p>
                          </div>
                        )}

                        <div className="rounded-xl border border-amber-200/60 bg-white/70 p-4 mt-5">
                          <p className="text-sm font-semibold text-stone-900 mb-3">Billing address</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input
                              value={billName}
                              onChange={(e) => setBillName(e.target.value)}
                              className={`${field} md:col-span-2`}
                              placeholder="Full name (required)"
                              aria-invalid={!billName ? 'true' : 'false'}
                            />
                            <input
                              value={billAddr1}
                              onChange={(e) => setBillAddr1(e.target.value)}
                              className={`${field} md:col-span-2`}
                              placeholder="Address line 1 (required)"
                              aria-invalid={!billAddr1 ? 'true' : 'false'}
                            />
                            <input
                              value={billAddr2}
                              onChange={(e) => setBillAddr2(e.target.value)}
                              className={`${field} md:col-span-2`}
                              placeholder="Address line 2 (optional)"
                            />
                            <input
                              value={billCity}
                              onChange={(e) => setBillCity(e.target.value)}
                              className={field}
                              placeholder="City (required)"
                              aria-invalid={!billCity ? 'true' : 'false'}
                            />
                            <input
                              value={billState}
                              onChange={(e) => setBillState(e.target.value)}
                              className={field}
                              placeholder="State / Province (required)"
                              aria-invalid={!billState ? 'true' : 'false'}
                            />
                            <input
                              value={billPostal}
                              onChange={(e) => setBillPostal(e.target.value)}
                              className={field}
                              placeholder="Postal code (required)"
                              aria-invalid={!billPostal ? 'true' : 'false'}
                            />
                            <input
                              value={billCountry}
                              onChange={(e) => setBillCountry(e.target.value)}
                              className={field}
                              placeholder="Country or region (required)"
                              aria-invalid={!billCountry ? 'true' : 'false'}
                            />
                          </div>
                        </div>
                        {confirmMode === 'paid' && (
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              disabled={busy || (isUnder13 && !consentConfirmed)}
                              onClick={() => {
                                if (busy) return;
                                const cardValid = payMethod === 'card' ? isValidCard() : isValidUpi();
                                const billingValid = isValidBilling();
                                const consentValid = !isUnder13 || consentConfirmed;
                                if (!canPay) {
                                  if (!cardValid) setFormError('Please fill all payment details correctly.');
                                  else if (!billingValid) setFormError('Please fill all billing address fields.');
                                  else if (!consentValid) setFormError('Parent consent is required for children under 13. Please complete the consent form first.');
                                  else setFormError('Please fill all mandatory details to proceed with the subscription.');
                                  return;
                                }
                                setFormError('');
                                handleCheckout();
                              }}
                              className={`text-white font-semibold px-4 py-2 rounded-lg ${
                                busy || (isUnder13 && !consentConfirmed)
                                  ? 'bg-gray-400'
                                  : 'art-btn-primary'
                              }`}
                            >
                              <span>
                                {(() => {
                                  const plans = effectiveIsMentor
                                    ? generateMentorPlans(effectiveMentorCount, isSingleMentor)
                                    : studentPlans;
                                  const selectedPlanData = plans.find((p) => p.id === selectedPlan);
                                  if (selectedPlanData && selectedPlanData.requiresPayment === false) {
                                    return 'Redeem Artmo Code';
                                  }
                                  return 'Confirm & Process Payment';
                                })()}
                                {' '}
                                Pay and Subscribe
                              </span>
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              </>
            )}

            {/* Consent block for under-13 ONLY (payment gating) - For both paid plans and Artmo Code */}
            {(confirmMode === 'paid' || confirmMode === 'artmocode') && isUnder13 && (
              <div className={`art-callout-strip backdrop-blur-sm shadow-lg ${confirmMode === 'artmocode' ? 'mx-4 mt-3 rounded-xl p-4 sm:mx-6' : 'mt-6 rounded-2xl p-6'}`}>
                <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${confirmMode === 'artmocode' ? 'mb-0' : 'mb-4'}`}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className={`auth-premium-callout-icon shrink-0 ${confirmMode === 'artmocode' ? 'h-9 w-9' : 'h-10 w-10'}`}>
                      <svg className={`${confirmMode === 'artmocode' ? 'w-4 h-4' : 'w-5 h-5'} text-[#6b5a48]`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div>
                      <div className={`${confirmMode === 'artmocode' ? 'text-base' : 'text-lg'} font-bold text-[#2f251b]`}>Consent & Privacy Settings</div>
                      <div className={`${confirmMode === 'artmocode' ? 'text-xs' : 'text-sm'} text-[#6b5a48]`}>Parent approval required for children under 13.</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowConsent(true)} 
                    className={`${confirmMode === 'artmocode' ? 'px-4 py-2 rounded-lg text-xs sm:text-sm shrink-0' : 'px-6 py-3 rounded-xl text-sm'} font-semibold art-btn-primary transition-all duration-200 shadow-lg hover:shadow-xl`}
                  >
                    {consentConfirmed ? 'Review Settings' : 'Open Consent Form'}
                  </button>
                </div>
                {!consentConfirmed && (
                  <div className={`flex items-center gap-2 text-red-700 ${confirmMode === 'artmocode' ? 'text-xs p-3 mt-2' : 'text-sm p-4'} bg-red-50/90 rounded-xl border-2 border-red-200/60`}>
                    <svg className="w-5 h-5 shrink-0 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <circle cx="12" cy="12" r="10" />
                      <path strokeLinecap="round" d="M12 8v4M12 16h.01" />
                    </svg>
                    <div>
                      <div className="font-semibold">Consent Required</div>
                      <div className="text-xs">Parent must confirm privacy settings before proceeding with payment.</div>
                    </div>
                  </div>
                )}
                {consentConfirmed && (
                  <div className={`flex items-center gap-2 text-green-700 ${confirmMode === 'artmocode' ? 'text-xs p-3 mt-2' : 'text-sm p-4'} bg-green-50/90 rounded-xl border-2 border-green-200/60`}>
                    <svg className="w-5 h-5 shrink-0 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <div>
                      <div className="font-semibold">Consent Confirmed</div>
                      <div className="text-xs">Privacy settings have been approved by parent/guardian.</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {confirmMode !== 'paid' && (
              <div className={`${confirmMode === 'artmocode' ? 'mt-3 flex justify-end gap-3 px-4 sm:px-6 pb-4' : 'mt-5 flex justify-end gap-3'}`}>
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-lg border border-amber-300 text-[#6b5a48] bg-white/80 hover:bg-white">Cancel</button>
              {confirmMode === 'ask' ? (
                <button onClick={async () => { 
                  // Check if this is an Artmo Code plan (no payment required)
                  const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
                  const selectedPlanData = plans.find(p => p.id === selectedPlan);
                  
                  if (selectedPlanData && selectedPlanData.requiresPayment === false) {
                    // Artmo Code - refresh consent status and check
                    if (isUnder13) {
                      console.log('🔄 Refreshing consent status before Artmo Code activation...');
                      await loadConsentStatus();
                      // Wait a bit for state to update
                      await new Promise(resolve => setTimeout(resolve, 200));
                    }
                    
                    // Artmo Code - check consent first for under-13 users
                    if (isUnder13 && !consentConfirmed) {
                      console.log('❌ Consent not confirmed:', { isUnder13, consentConfirmed });
                      setFormError('Parent consent is required for children under 13 before redeeming an Artmo Code. Please complete the consent form first.');
                      return;
                    }
                    
                    console.log('✅ Consent confirmed, proceeding with Artmo Code activation:', { isUnder13, consentConfirmed });
                    // Artmo Code - activate directly without switching to paid checkout
                    handleCheckout();
                  } else {
                    // Paid plan (Basic, Pro) - proceed to checkout
                    setConfirmMode('paid');
                  }
                }} disabled={busy || (isUnder13 && !consentConfirmed && selectedPlan === 'artmocode')} className={`px-4 py-2 rounded-lg font-semibold ${busy || (isUnder13 && !consentConfirmed && selectedPlan === 'artmocode') ? 'bg-gray-400 text-white' : 'art-btn-primary'}`}>Continue</button>
              ) : (
                <button
                  onClick={async () => {
                    // Check if this is an Artmo Code plan - skip payment validation
                    const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
                    const selectedPlanData = plans.find(p => p.id === selectedPlan);
                    
                    if (selectedPlanData && selectedPlanData.requiresPayment === false) {
                      // Artmo Code - refresh consent status and check
                      if (isUnder13) {
                        console.log('🔄 Refreshing consent status before Artmo Code activation (paid mode)...');
                        await loadConsentStatus();
                        // Wait a bit for state to update
                        await new Promise(resolve => setTimeout(resolve, 200));
                      }
                      
                      // Artmo Code - check consent first for under-13 users
                      if (isUnder13 && !consentConfirmed) {
                        console.log('❌ Consent not confirmed (paid mode):', { isUnder13, consentConfirmed });
                        setFormError('Parent consent is required for children under 13 before redeeming an Artmo Code. Please complete the consent form first.');
                        return;
                      }
                      
                      console.log('✅ Consent confirmed, proceeding with Artmo Code (paid mode):', { isUnder13, consentConfirmed });
                      // Artmo Code - no payment required, just activate
                      handleCheckout();
                      return;
                    }
                    
                    // For paid plans (Basic, Pro, Artmo Code), require payment details
                    // Debug validation
                    const cardValid = payMethod === 'card' ? isValidCard() : isValidUpi();
                    const billingValid = isValidBilling();
                    const consentValid = !isUnder13 || consentConfirmed;
                    
                    console.log('🔍 Payment validation debug:', {
                      canPay,
                      cardValid,
                      billingValid,
                      consentValid,
                      isUnder13,
                      consentConfirmed,
                      payMethod,
                      cardEmail: cardEmail.trim(),
                      cardName: cardName.trim(),
                      cardNumber: cardNumber.replace(/\D/g, '').length,
                      cardExp,
                      cardCvc: cardCvc.trim().length,
                      billName: billName.trim(),
                      billAddr1: billAddr1.trim(),
                      billCity: billCity.trim(),
                      billState: billState.trim(),
                      billPostal: billPostal.trim(),
                      billCountry: billCountry.trim()
                    });
                    
                    if (!canPay) {
                      if (!cardValid) {
                        setFormError('Please fill all payment details correctly.');
                      } else if (!billingValid) {
                        setFormError('Please fill all billing address fields.');
                      } else if (!consentValid) {
                        setFormError('Parent consent is required for children under 13. Please complete the consent form first.');
                      } else {
                      setFormError('Please fill all mandatory details to proceed with the subscription.');
                      }
                      return;
                    }
                    console.log('🎯 MEMBERSHIP - Button clicked!');
                    console.log('🎯 MEMBERSHIP - selectedPlan:', selectedPlan);
                    console.log('🎯 MEMBERSHIP - confirmMode:', confirmMode);
                    console.log('🎯 MEMBERSHIP - isUnder13:', isUnder13);
                    console.log('🎯 MEMBERSHIP - consentConfirmed:', consentConfirmed);
                    
                    // Check consent for paid plans and Artmo Code for under-13 users
                    if (isUnder13 && !consentConfirmed) {
                      setFormError('Parent consent is required for children under 13. Please complete the consent form first.');
                      return;
                    }
                    setFormError('');
                    
                    // Prevent duplicate checkout if already processing
                    if (busy) {
                      console.log('⚠️ Checkout already in progress, ignoring duplicate call');
                      return;
                    }
                    
                    // For mentor plans, process payment directly (no separate checkout page)
                    if (selectedPlan === 'mentor-basic' || selectedPlan === 'mentor-pro' || selectedPlan === 'mentor-premium') {
                      console.log('🎯 MEMBERSHIP - Processing mentor plan payment directly:', selectedPlan);
                      console.log('🎯 MEMBERSHIP - Mentor data:', {
                        plan: selectedPlan,
                        mentorType: user?.mentorType,
                        mentorCount: mentorCount,
                        userData: { uid: user.uid, email: user.email }
                      });
                      handleCheckout();
                      return;
                    }
                    
                    // For student plans, process payment directly (no separate checkout page needed)
                    // The checkout page is for book orders, not membership subscriptions
                    if (selectedPlan === 'basic' || selectedPlan === 'base' || selectedPlan === 'pro' || selectedPlan === 'premium') {
                      console.log('🎯 MEMBERSHIP - Processing student plan payment directly:', selectedPlan);
                      handleCheckout();
                      return;
                    }
                    
                    handleCheckout();
                  }}
                  disabled={busy || ((confirmMode === 'paid' || confirmMode === 'artmocode' || (confirmMode === 'ask' && selectedPlan === 'artmocode')) && isUnder13 && !consentConfirmed)}
                  className={`text-white font-semibold ${
                    confirmMode === 'artmocode'
                      ? `art-btn-primary px-6 py-3 rounded-2xl text-base shadow-lg ${busy || ((confirmMode === 'paid' || confirmMode === 'artmocode' || (confirmMode === 'ask' && selectedPlan === 'artmocode')) && isUnder13 && !consentConfirmed) ? 'opacity-50 cursor-not-allowed' : ''}`
                      : `art-btn-primary px-4 py-2 rounded-lg ${busy || ((confirmMode === 'paid' || confirmMode === 'artmocode' || (confirmMode === 'ask' && selectedPlan === 'artmocode')) && isUnder13 && !consentConfirmed) ? 'opacity-50 cursor-not-allowed' : ''}`
                  }`}
                >
                  {(() => {
                    // Check if this is an Artmo Code plan (no payment required)
                    const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
                    const selectedPlanData = plans.find(p => p.id === selectedPlan);
                    
                    if (confirmMode === 'artmocode') return 'Redeem Artmo Code';
                    if (selectedPlanData && selectedPlanData.requiresPayment === false) {
                      return 'Redeem Artmo Code';
                    }
                    return 'Confirm & Process Payment';
                  })()}
                  {confirmMode === 'artmocode' ? '' : 'Pay and Subscribe'}
                </button>
              )}
            </div>
            )}
            {/* Error display inside modal */}
            {formError && (
              <div className={`${confirmMode === 'artmocode' ? 'mt-2 mx-4 sm:mx-6' : 'mt-4'} p-4 bg-red-50 border-2 border-red-200 rounded-xl`}>
                <p className="text-red-800 text-sm font-semibold">{formError}</p>
              </div>
            )}
            {/* Inline loader overlay for payment processing */}
            {busy && (() => {
              // Check if this is Artmo Code redemption (no payment required)
              const plans = effectiveIsMentor ? generateMentorPlans(effectiveMentorCount, isSingleMentor) : studentPlans;
              const selectedPlanData = plans.find(p => p.id === selectedPlan);
              const isArtmoCode = confirmMode === 'artmocode' || (selectedPlanData && selectedPlanData.requiresPayment === false);
              
              return (
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  {isArtmoCode ? (
                    <div className="w-full max-w-md rounded-2xl shadow-2xl border border-amber-200 bg-white/95 backdrop-blur-sm p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-lg" aria-hidden>
                          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                            <path d="M12 6h6" />
                            <path d="M12 10h6" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[#6b5a48]">Base access (Artmo Code)</div>
                          <div className="text-3xl font-extrabold text-amber-900">Free</div>
                        </div>
                      </div>

                      <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-4">
                        <div className="flex items-start gap-3">
                          <div className="relative">
                            <svg className="animate-spin h-9 w-9 text-[#8b6914]" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="h-3 w-3 bg-amber-500 rounded-full animate-pulse" />
                            </div>
                          </div>

                          <div className="pt-1">
                            <p className="text-amber-900 font-semibold text-lg">
                              Hang tight — we&apos;re unlocking your Studio time…
                            </p>
                            <p className="text-[#8b6914] text-sm mt-1">
                              Checking your code and adding your months. Almost there!
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 rounded-xl border border-amber-100 bg-white/60 p-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#6b5a48] font-semibold">Amount</span>
                          <span className="text-amber-900 font-bold">₹0.00</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-[#6b5a48] font-semibold">GST (18%)</span>
                          <span className="text-amber-900 font-bold">₹0.00</span>
                        </div>
                        <div className="flex justify-between text-base font-bold text-amber-950 mt-2">
                          <span>Total due today</span>
                          <span>₹0.00</span>
                        </div>
                      </div>

                      <p className="mt-3 text-xs text-[#8b6914] text-center">
                        This will only take a few seconds.
                      </p>
                    </div>
                  ) : (
                    <div className="w-full max-w-sm rounded-2xl border-2 border-amber-200 bg-white/95 backdrop-blur-sm p-6 shadow-2xl flex flex-col items-center gap-4">
                      <div className="relative">
                        <svg className="animate-spin h-8 w-8 text-[#8b6914]" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                      </div>
                      <div className="text-center">
                        <p className="text-amber-900 font-semibold text-lg">Processing payment...</p>
                        <p className="text-[#8b6914] text-sm mt-1">Please wait while we process your payment</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Inline success message with next actions (no redirect) */}
            {successMsg && (
              <div className="mt-4 p-4 rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800">
                <div className="font-bold mb-2">{successMsg}</div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={async () => {
                      try {
                        if (isMentor) {
                          // Check if this is a single mentor
                          if (user?.mentorType === 'single') {
                            navigate('/mentor-dashboard');
                          } else {
                            navigate('/mentor');
                          }
                        } else {
                          // Student users - check if profile exists, then redirect accordingly
                          const profileSnap = await getDoc(doc(db, 'artistProfiles', user.uid));
                          if (profileSnap.exists()) {
                            navigate('/my-art-world');
                          } else {
                            navigate('/artist-profile', { state: { paymentSuccess: true } });
                          }
                        }
                      } catch (_) {
                        if (isMentor) {
                          if (user?.mentorType === 'single') {
                            navigate('/mentor-dashboard');
                          } else {
                            navigate('/mentor');
                          }
                        } else {
                          // Redirect directly to artist profile creation page (Welcome, Future Artist!)
                          navigate('/artist-profile', { state: { paymentSuccess: true } });
                        }
                      } finally {
                        setSuccessMsg('');
                        setConfirmOpen(false);
                      }
                    }}
                    className="px-4 py-2 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 text-sm font-semibold"
                  >
                    Continue
                  </button>
                  <button
                    onClick={() => { setSuccessMsg(''); setConfirmOpen(false); }}
                    className="px-4 py-2 rounded-lg border border-emerald-300 text-emerald-800 bg-white text-sm font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
            {formError && (
              <p className="mt-3 text-sm text-red-600">{formError}</p>
            )}
          </div>
        </div>
      )}

      {successMsg && (
        <div className="fixed bottom-4 inset-x-0 flex justify-center z-50">
          <div className="bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg">{successMsg}</div>
        </div>
      )}

      {/* Simplified Consent Modal for Under-13 Users */}
      {showConsent && (
        <SimpleConsentForm
          onAgree={handleConsentAgree}
          onCancel={() => setShowConsent(false)}
          isSubmitting={consentSubmitting}
        />
      )}
      </div>
    </React.Fragment>
  );
}

